import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Calendar, ArrowRight, X, ExternalLink, BookOpen, Heart, Award, CheckCircle2, HelpCircle } from 'lucide-react';
import SEO from '../components/SEO';
import RejsetipsGuideContent from '../components/RejsetipsGuideContent';
import { useLanguage } from '../components/LanguageContext';
import { inspectionArticles_da, inspectionArticles_en } from '../hotelInspectionsData';
import { buildArticleSchema, buildBreadcrumbSchema, buildOrganizationSchema, buildBlogWithArticlesSchema } from '../lib/schema';

// Artikel-data (Danish)
export const articles_da = [
  {
    id: 'invisible-disabilities-badge-spanien',
    title: 'Ny regel i spanske lufthavne: Solsikkesnoren er ikke længere nok',
    date: 'maj 2025',
    category: 'Lufthavn & assistance',
    categories: ['Lufthavn & assistance', 'Praktisk info'],
    intro: 'Aena-lufthavnene i Spanien har indført et gratis “Invisible Disabilities Badge”. Læs hvad det betyder for dig, og hvordan du ansøger inden rejsen.',
    image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/invisible-disabilities-airport.jpg',
    imageLarge: 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/invisible-disabilities-airport.jpg',
    content: (
      <div className="space-y-6 text-slate-700 leading-relaxed">
        <h3 className="text-xl md:text-2xl font-serif font-bold text-slate-800 border-b border-emerald-100 pb-2">
          Vigtig information til rejsende med usynlige handicap i Spanien
        </h3>
        <p>
          Skal du rejse gennem Palma de Mallorca eller en anden spansk Aena-lufthavn, og har du eller en af dine medrejsende en usynlig funktionsnedsættelse som f.eks. autisme, ADHD, angst, PTSD, demens eller lignende?
        </p>
        <p>
          Så er der en vigtig ændring, som mange endnu ikke kender til.
        </p>
        <p>
          Den velkendte Solsikkesnor, også kendt som Hidden Disabilities Sunflower Lanyard, giver ikke længere adgang til Aenas særlige sikkerhedskøer i de deltagende spanske lufthavne.
        </p>
        <p>
          I stedet har Aena indført deres eget gratis Invisible Disabilities Badge, som kan ansøges online inden rejsen.
        </p>

        <div className="bg-emerald-50 border border-emerald-100 p-6 rounded-2xl my-6">
          <h4 className="font-bold text-emerald-800 mb-3">Badget kan:</h4>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Gøre det lettere for lufthavnspersonalet at identificere passagerer, som kan have behov for ekstra støtte</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Give adgang til særlige sikkerhedskøer, hvor disse findes</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Vises på mobiltelefon eller printes</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Bruges af en familie eller rejsegruppe, da der kun behøves ét badge pr. familie eller rejsegruppe</span>
            </li>
          </ul>
        </div>

        <div className="bg-amber-50 border border-amber-100 p-6 rounded-2xl my-6">
          <h4 className="font-bold text-amber-800 mb-3">Vigtigt at vide</h4>
          <ul className="space-y-2 text-amber-900">
            <li className="flex items-start gap-2">
              <span className="font-bold text-amber-600 mr-1">•</span>
              <span>Badget er ikke det samme som Fast Track</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-amber-600 mr-1">•</span>
              <span>Det fritager ikke for sikkerhedskontrol</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-amber-600 mr-1">•</span>
              <span>Det erstatter ikke bestilt lufthavnsassistance</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-amber-600 mr-1">•</span>
              <span>Det gælder kun på rejsedagen</span>
            </li>
          </ul>
        </div>

        <h4 className="font-bold text-slate-800 mt-6">Lufthavne hvor ordningen blandt andet findes</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 my-2">
          {['Palma de Mallorca', 'Alicante', 'Barcelona', 'Málaga', 'Ibiza', 'Menorca', 'Lanzarote', 'Fuerteventura', 'Gran Canaria', 'Tenerife South'].map((airport) => (
            <div key={airport} className="bg-stone-50 px-3 py-2 rounded-lg border border-stone-100 text-sm font-medium text-slate-700">
              {airport}
            </div>
          ))}
        </div>

        <p className="text-sm text-slate-500 italic mt-4">
          Ansøgning bør foretages mindst 48 timer før afrejse.
        </p>

        <div className="pt-4">
          <a
            href="https://www.aena.es/en/passengers/travellers/passengers-with-special-needs/invisible-disabilities/invisible-disabilities-badge.html"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#99A18C] text-white px-6 py-3.5 rounded-xl font-bold hover:bg-[#8a917e] transition-all shadow-md hover:shadow-lg"
          >
            Læs mere og ansøg hos Aena
            <ExternalLink size={16} />
          </a>
        </div>

        <p className="pt-6 border-t border-slate-100 italic text-slate-600">
          Hos Alora Rejser ved vi, at tryghed på rejsen betyder meget. Derfor deler vi gerne information, der kan gøre rejsen lidt lettere for personer med både synlige og usynlige handicap.
        </p>
      </div>
    )
  },
  {
    id: 'disneyland-paris-i-koerestol',
    title: 'Disney i kørestol?',
    date: 'juni 2025',
    category: 'Handicaprejser',
    categories: ['Handicaprejser', 'Destinationer'],
    intro: 'Disneyland Paris er blandt de mest tilgængelige forlystelsesparker i Europa. Få overblik over Priority Card, rabat, kørestolsleje, hoteller og transport.',
    image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/disneyland-wheelchair.jpg',
    imageLarge: 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/disneyland-wheelchair.jpg',
    content: (
      <div className="space-y-6 text-slate-700 leading-relaxed">
        <h3 className="text-xl md:text-2xl font-serif font-bold text-slate-800 border-b border-emerald-100 pb-2">
          Disney i kørestol?
        </h3>
        <p>
          Et besøg i Disneyland Paris kan være en fantastisk oplevelse for kørestolsbrugere. Parkerne er blandt de mest tilgængelige i Europa og tilbyder løsninger, der gør det lettere at opleve forlystelser, shows, parader og restauranter.
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Priority Card – det vigtigste at kende</h4>
        <p>
          Hvis du har et handicap eller nedsat mobilitet, anbefales det at ansøge om et Priority Card før dit besøg.
        </p>
        <div className="bg-emerald-50 border border-emerald-100 p-5 rounded-xl">
          <p className="font-semibold text-emerald-800 mb-2">Kortet giver prioriteret adgang til:</p>
          <ul className="grid grid-cols-2 gap-2 text-sm text-emerald-900">
            <li className="flex items-center gap-2">✔ Forlystelser</li>
            <li className="flex items-center gap-2">✔ Shows</li>
            <li className="flex items-center gap-2">✔ Parader</li>
            <li className="flex items-center gap-2">✔ Karaktermøder</li>
            <li className="flex items-center gap-2">✔ Udvalgte områder</li>
          </ul>
        </div>
        <p className="text-sm">
          Ansøgning sker online:{' '}
          <a
            href="https://www.disneylandparis.com/en-int/guest-services/priority-card/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#99A18C] font-semibold underline hover:text-[#8a917e]"
          >
            Ansøg her <ExternalLink size={12} className="inline" />
          </a>
          <br />
          Ved ankomst skal du medbringe gyldig dokumentation på dit handicap.
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Rabat på parkbilletter</h4>
        <p>
          Gæster med handicap kan ofte få rabat på parkbilletter til sig selv og én ledsager.
          <br />
          <a
            href="https://tickets.disneylandparis.com/en-gb/tickets-guests-with-disabilities"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#99A18C] font-semibold underline hover:text-[#8a917e]"
          >
            Læs mere om rabat her <ExternalLink size={12} className="inline" />
          </a>
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Leje af kørestol i parken</h4>
        <p>
          Kørestole kan lejes direkte i parkerne. Udlejningen findes ved:
        </p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li>Town Square Terrace i Disneyland Park</li>
          <li>Studio Services i Walt Disney Studios Park</li>
        </ul>
        <p>
          Udlejningen sker efter først-til-mølle-princippet, og der betales et mindre dagligt gebyr samt et refunderbart depositum.
          <br />
          <a
            href="https://www.disneylandparis.com/en-int/guest-services/accessibility-at-disneylandparis/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#99A18C] font-semibold underline hover:text-[#8a917e]"
          >
            Læs mere om tilgængelighed her <ExternalLink size={12} className="inline" />
          </a>
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Attraktioner og tilgængelighed</h4>
        <p>
          Det varierer fra attraktion til attraktion, om man kan blive i kørestolen, eller om der kræves forflytning. Derfor anbefaler vi altid at tjekke den aktuelle tilgængelighed hos Disneyland Paris før besøget.
        </p>

        <div className="space-y-6">
          {/* Attraktioner hvor mange kørestolsbrugere kan deltage */}
          <div className="bg-emerald-50/50 border border-emerald-100 p-6 rounded-2xl">
            <h5 className="font-serif font-bold text-emerald-900 text-base mb-2">
              Attraktioner hvor mange kørestolsbrugere kan deltage
            </h5>
            <p className="text-xs text-emerald-800/80 mb-4">
              Her kan man ofte deltage direkte i kørestolen eller med minimal forflytning.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h6 className="font-bold text-xs text-emerald-800 uppercase tracking-wider mb-2 border-b border-emerald-100 pb-1">
                  Disneyland Park
                </h6>
                <ul className="text-sm space-y-1.5 text-slate-700">
                  <li>• It’s a Small World</li>
                  <li>• Pirates of the Caribbean</li>
                  <li>• Phantom Manor</li>
                  <li>• Buzz Lightyear Laser Blast</li>
                  <li>• Mickey’s PhilharMagic</li>
                  <li>• Le Passage Enchanté d’Aladdin</li>
                  <li>• La Tanière du Dragon</li>
                  <li>• Les Mystères du Nautilus</li>
                  <li>• Thunder Mesa Riverboat Landing</li>
                  <li>• Disneyland Railroad, udvalgte stationer</li>
                </ul>
              </div>
              <div>
                <h6 className="font-bold text-xs text-emerald-800 uppercase tracking-wider mb-2 border-b border-emerald-100 pb-1">
                  Walt Disney Studios Park / Disney Adventure World
                </h6>
                <ul className="text-sm space-y-1.5 text-slate-700">
                  <li>• Ratatouille: The Adventure</li>
                  <li>• Cars ROAD TRIP</li>
                  <li>• Spider-Man W.E.B. Adventure</li>
                  <li>• Animation Academy</li>
                  <li>• Disney Junior Dream Factory</li>
                  <li>• Mickey and the Magician</li>
                  <li>• Together: A Pixar Musical Adventure</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Attraktioner som normalt kræver forflytning */}
          <div className="bg-amber-50/40 border border-amber-100 p-6 rounded-2xl">
            <h5 className="font-serif font-bold text-amber-900 text-base mb-2">
              Attraktioner som normalt kræver forflytning fra kørestolen
            </h5>
            <p className="text-xs text-amber-800/80 mb-4">
              For disse forlystelser skal man kunne flytte sig over i forlystelsens sæde (selv eller med hjælp fra en ledsager).
            </p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-slate-700">
              <li>• Big Thunder Mountain</li>
              <li>• Peter Pan’s Flight</li>
              <li>• Dumbo the Flying Elephant</li>
              <li>• Star Wars Hyperspace Mountain</li>
              <li>• Orbitron</li>
              <li>• Mad Hatter’s Tea Cups</li>
              <li>• Indiana Jones and the Temple of Peril</li>
              <li>• Star Tours</li>
              <li>• RC Racer</li>
              <li>• Crush’s Coaster</li>
              <li>• The Twilight Zone Tower of Terror</li>
            </ul>
          </div>

          {/* Shows og oplevelser med god tilgængelighed */}
          <div className="bg-stone-50 border border-stone-200 p-6 rounded-2xl">
            <h5 className="font-serif font-bold text-slate-900 text-base mb-2">
              Shows og oplevelser med god tilgængelighed
            </h5>
            <p className="text-xs text-slate-500 mb-4">
              Disse oplevelser har dedikerede kørestolspladser og fremragende adgangsforhold.
            </p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-slate-700">
              <li>• Disney Stars on Parade</li>
              <li>• Aftenfyrværkeri og droneshow</li>
              <li>• Karaktermøder</li>
              <li>• Mickey and the Magician</li>
              <li>• The Lion King: Rhythms of the Pride Lands</li>
              <li>• Together: A Pixar Musical Adventure</li>
              <li>• Disney Junior Dream Factory</li>
            </ul>
          </div>
        </div>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Restauranter og butikker</h4>
        <p>
          De fleste restauranter og butikker har niveaufri adgang og brede døre. Ved restauranter med bordservering anbefales det at reservere bord via Disneyland Paris-appen.
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Transport og handicapvenlige hoteller</h4>
        <p>
          De officielle Disney-hoteller tilbyder handicapvenlige værelser med tilgængelige badeværelser. De gratis shuttlebusser mellem hotellerne og parkerne har kørestolsramper.
          <br />
          Tager du toget fra Paris, er RER A den nemmeste løsning. Stationen Marne-la-Vallée/Chessy ligger direkte ved parkens indgang.
        </p>

        <div className="bg-emerald-900 text-white p-6 rounded-2xl mt-8">
          <h4 className="font-serif font-bold text-xl mb-3 text-emerald-100 italic">Alora Rejsers tip</h4>
          <p className="text-emerald-50 mb-4">
            Disneyland Paris er en af de destinationer i Europa, hvor tilgængeligheden generelt fungerer rigtig godt for kørestolsbrugere.
          </p>
          <p className="font-semibold text-emerald-200 mb-2">Vores bedste råd er at:</p>
          <ul className="space-y-1 text-sm text-emerald-100">
            <li>• Ansøge om Priority Card hjemmefra</li>
            <li>• Medbringe dokumentation</li>
            <li>• Booke restauranter i god tid</li>
            <li>• Undersøge attraktionernes adgangskrav på forhånd</li>
          </ul>
        </div>
      </div>
    )
  },
  {
    id: 'handicapvenlige-strande-paa-mallorca',
    title: 'Handicapvenlige strande på Mallorca',
    date: 'juni 2025',
    category: 'Destinationer',
    categories: ['Destinationer', 'Handicaprejser', 'Praktisk info'],
    intro: 'Mallorca har flere strande med ramper, handicaptoiletter, badekørestole og strandhjælp. Få overblik over de bedste områder.',
    image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/378a20d8-af7c-4ed6-9273-84c31896612e.jpg',
    imageLarge: 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/378a20d8-af7c-4ed6-9273-84c31896612e.jpg',
    content: (
      <div className="space-y-6 text-slate-700 leading-relaxed">
        <h3 className="text-xl md:text-2xl font-serif font-bold text-slate-800 border-b border-emerald-100 pb-2">
          Handicapvenlige strande på Mallorca
        </h3>
        <p>
          Mallorca er en af de mest handicapvenlige destinationer i Sydeuropa. Flere af øens strande tilbyder ramper, handicaptoiletter, badekørestole og strandhjælp, så også personer med nedsat mobilitet kan nyde en dag ved havet.
        </p>
        <p>
          Hos Alora Rejser ved vi, at en strand ikke nødvendigvis er handicapvenlig, bare fordi der er en promenade. Derfor ser vi også på adgang til vandet, toiletforhold, terræn og muligheder for hjælp på stranden.
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Hvad er strandhjælp?</h4>
        <p>
          Strandhjælp er en gratis service, som flere kommuner på Mallorca tilbyder i sommermånederne.
        </p>
        <div className="bg-emerald-50 border border-emerald-100 p-5 rounded-xl">
          <p className="font-semibold text-emerald-800 mb-2">Her kan du typisk få hjælp af uddannet personale til:</p>
          <ul className="space-y-1.5 text-sm text-emerald-900">
            <li>• At komme fra promenaden ned på stranden</li>
            <li>• At benytte en badekørestol, også kaldet amfibiestol</li>
            <li>• At komme sikkert i og op af vandet</li>
            <li>• Praktisk hjælp ved strandområdet</li>
          </ul>
        </div>
        <p>
          På de fleste strande findes der også handicaptoiletter og ramper tæt ved strandhjælpsområdet. Faciliteterne er normalt tilgængelige fra juni til september, men åbningstider og placering kan variere fra år til år.
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Alcúdia Strand</h4>
        <p>
          Alcúdia er en af de bedste strande på Mallorca for kørestolsbrugere. Den brede sandstrand kombineret med fladt terræn gør området meget tilgængeligt.
        </p>
        <ul className="grid grid-cols-2 gap-2 text-sm bg-stone-50 p-4 rounded-xl border border-stone-100">
          <li>✔ Handicaptoiletter</li>
          <li>✔ Træramper mod vandet</li>
          <li>✔ Handicapparkering</li>
          <li>✔ Strandhjælp i sommersæsonen</li>
          <li>✔ Badekørestole</li>
          <li>✔ Roligt og lavt badevand</li>
        </ul>
        <p>
          De mest tilgængelige områder findes normalt ved de centrale strandafsnit nær havnen og promenaden. Alcúdia er særligt velegnet til familier med børn.
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Port de Pollença</h4>
        <p>
          Port de Pollença er et af vores personlige favoritområder på Mallorca. Den lange promenade gør det nemt at komme rundt, og området har generelt meget få bakker.
        </p>
        <ul className="grid grid-cols-2 gap-2 text-sm bg-stone-50 p-4 rounded-xl border border-stone-100">
          <li>✔ Ramper til stranden</li>
          <li>✔ Handicaptoiletter flere steder langs promenaden</li>
          <li>✔ Fladt terræn</li>
          <li>✔ Gode adgangsforhold til restauranter og caféer</li>
          <li>✔ Tilgængelige gangarealer langs havet</li>
          <li>✔ Strandhjælp i sommerperioden</li>
        </ul>
        <p>
          Med tilføjelsen af strandhjælp i sommerperioden er Port de Pollença nu på linje med de bedste områder på Mallorca og er et af de absolut letteste steder at holde ferie som kørestolsbruger.
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Cala Millor</h4>
        <p>
          Cala Millor er gennem mange år blevet udviklet med fokus på tilgængelighed. Den brede promenade og de mange faciliteter gør området populært blandt både familier og voksne.
        </p>
        <ul className="grid grid-cols-2 gap-2 text-sm bg-stone-50 p-4 rounded-xl border border-stone-100">
          <li>✔ Handicaptoiletter</li>
          <li>✔ Ramper til stranden</li>
          <li>✔ Strandhjælp i sommersæsonen</li>
          <li>✔ Badekørestole</li>
          <li>✔ Flad promenade langs hele stranden</li>
          <li>✔ Handicapparkering</li>
        </ul>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Sa Coma</h4>
        <p>
          Sa Coma ligger tæt ved Cala Millor og tilbyder ligeledes gode forhold for kørestolsbrugere.
        </p>
        <ul className="grid grid-cols-2 gap-2 text-sm bg-stone-50 p-4 rounded-xl border border-stone-100">
          <li>✔ Handicaptoiletter</li>
          <li>✔ Ramper til stranden</li>
          <li>✔ Strandhjælp i sommersæsonen</li>
          <li>✔ Badekørestole</li>
          <li>✔ Fladt terræn omkring promenaden</li>
          <li>✔ Gode adgangsforhold til butikker</li>
        </ul>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Playa de Palma</h4>
        <p>
          Playa de Palma ligger kun få kilometer fra lufthavnen og er derfor et populært valg for mange. Den lange strandpromenade gør området let at komme rundt i.
        </p>
        <ul className="grid grid-cols-2 gap-2 text-sm bg-stone-50 p-4 rounded-xl border border-stone-100">
          <li>✔ Handicaptoiletter</li>
          <li>✔ Ramper til stranden</li>
          <li>✔ Fladt terræn</li>
          <li>✔ Tilgængelige restauranter</li>
          <li>✔ Strandhjælp på udvalgte afsnit</li>
        </ul>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Hvilket område skal du vælge?</h4>
        <div className="space-y-6 bg-stone-50 p-6 rounded-2xl border border-stone-100">
          <div>
            <h5 className="font-bold text-emerald-900 text-base mb-1">Port de Pollença</h5>
            <p className="text-sm text-slate-600 leading-relaxed">
              Hvis du ønsker en hyggelig autentisk ferieby med flad promenade, rolige omgivelser og gode adgangsforhold mellem hotel, strand, restauranter og butikker, er Port de Pollença et fremragende valg.
            </p>
          </div>
          <div className="border-t border-stone-200/60 pt-4">
            <h5 className="font-bold text-emerald-900 text-base mb-1">Sa Coma</h5>
            <p className="text-sm text-slate-600 leading-relaxed">
              Hvis du ønsker en mere rolig by med bred sandstrand, lang strandpromenade og gode tilgængelige faciliteter tæt på hotellerne, er Sa Coma et populært valg.
            </p>
          </div>
          <div className="border-t border-stone-200/60 pt-4">
            <h5 className="font-bold text-emerald-900 text-base mb-1">Cala Millor</h5>
            <p className="text-sm text-slate-600 leading-relaxed">
              Hvis du ønsker et livligt ferieområde med mange restauranter, butikker og en lang, tilgængelig strandpromenade, er Cala Millor et godt valg.
            </p>
          </div>
          <div className="border-t border-stone-200/60 pt-4">
            <h5 className="font-bold text-emerald-900 text-base mb-1">Palma-kysten / Playa de Palma / Can Pastilla</h5>
            <p className="text-sm text-slate-600 leading-relaxed">
              Hvis du ønsker kort transfer fra lufthavnen, by og seværdigheder tæt på, en lang strandpromenade og nem adgang til Palma by, er Palma-kysten et oplagt valg.
            </p>
          </div>
        </div>

        <div className="bg-emerald-900 text-white p-6 rounded-2xl mt-8">
          <h4 className="font-serif font-bold text-xl mb-3 text-emerald-100 italic">Alora Rejsers tip</h4>
          <p className="text-emerald-50 mb-4">
            Når vi udvælger hoteller på Mallorca, ser vi ikke kun på hotellet. Vi undersøger også:
          </p>
          <ul className="space-y-1 text-sm text-emerald-100">
            <li>• Afstanden til stranden</li>
            <li>• Om der er trapper undervejs</li>
            <li>• Placering af handicaptoiletter</li>
            <li>• Mulighed for strandhjælp</li>
            <li>• Badekørestole og adgang til havet</li>
            <li>• Tilgængelige restauranter og butikker i nærområdet</li>
          </ul>
          <p className="text-emerald-200 text-sm mt-4 font-medium">
            På den måde får du et mere realistisk billede af, hvor nemt det faktisk er at holde ferie som kørestolsbruger.
          </p>
        </div>

        <p className="pt-6 border-t border-slate-100 italic text-slate-600">
          Har du spørgsmål til handicapvenlige strande eller hoteller på Mallorca, er du altid velkommen til at kontakte Alora Rejser.
        </p>
      </div>
    )
  },
  {
    id: 'handicapvenlige-strande-paa-kreta',
    title: 'Handicapvenlige strande på Kreta – hvor kan du komme helt ned til vandet i kørestol?',
    date: 'september 2025',
    category: 'Destinationer',
    categories: ['Destinationer', 'Handicaprejser', 'Praktisk info'],
    intro: 'Kreta har nogle fantastiske strande, men bruger du kørestol eller er gangbesværet, er en smuk strand ikke nødvendigvis en strand, du kan bruge. Få en guide til de bedste muligheder med SEATRAC og strandstier.',
    image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/seatrac-handicapvenlig-strand-kreta.jpg',
    imageLarge: 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/seatrac-handicapvenlig-strand-kreta.jpg',
    content: (
      <div className="space-y-6 text-slate-700 leading-relaxed">
        <h3 className="text-xl md:text-2xl font-serif font-bold text-slate-800 border-b border-emerald-100 pb-2">
          Handicapvenlige strande på Kreta – hvor kan du komme helt ned til vandet i kørestol?
        </h3>
        <p>
          Kreta har nogle fantastiske strande, men bruger du kørestol eller er gangbesværet, er en smuk strand ikke nødvendigvis en strand, du kan bruge.
        </p>
        <p>
          Sandet kan være en næsten umulig forhindring, og selv ganske få meter fra en promenade til vandkanten kan betyde, at man ikke kommer længere.
        </p>
        <p>
          Heldigvis findes der flere steder på Kreta, hvor tilgængeligheden er tænkt langt bedre ind. På en række strande findes SEATRAC, strandstier over sandet, handicaptoilet, parkering, omklædning, bruser og skyggeområder.
        </p>
        <p>
          Her får du en guide til nogle af de bedste muligheder. Jeg har inkluderet det meste af Kreta, selvom vi ikke har hoteller over det hele, kan en dagstur jo også være en fantastisk mulighed⛱️ ❤️
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Først: Hvad er SEATRAC?</h4>
        <p>
          SEATRAC er et system, som giver personer med nedsat mobilitet mulighed for at komme fra stranden og direkte ud i havet.
        </p>
        <p>
          Man sætter sig på et sæde, som kører på en skinne ud i vandet. Systemet er udviklet, så brugeren i mange tilfælde kan komme i havet mere selvstændigt og uden at skulle bæres gennem sandet.
        </p>
        <p>
          Det er gratis at benytte.
        </p>
        <p>
          Det er dog vigtigt at skelne mellem SEATRAC og en strandsti.
        </p>
        <p>
          SEATRAC hjælper dig det sidste stykke ud i selve vandet, mens en strandsti eller strandmåtte gør det muligt at køre med kørestol hen over sandet mod vandet og SEATRAC-anlægget.
        </p>
        <p>
          For mange kørestolsbrugere er kombinationen af de to derfor afgørende.
        </p>

        <div className="bg-stone-50 border border-stone-200/80 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-bold text-slate-900">⭐ Agioi Apostoloi – Chania</h4>
          <p>
            Agioi Apostoloi vest for Chania er efter min vurdering en af de mest interessante strande at kigge på, hvis tilgængeligheden har stor betydning for ferien.
          </p>
          <p>
            Her findes to SEATRAC-anlæg, og der er samtidig registreret en lang række tilgængelige faciliteter.
          </p>
          <div>
            <p className="font-semibold text-slate-800 mb-2">Dokumenterede faciliteter:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
              <li>♿ SEATRAC – ja</li>
              <li>♿ Strandsti over sandet – ja</li>
              <li>♿ Parkering – ja</li>
              <li>♿ Toilet – ja</li>
              <li>♿ Handicapvenlig omklædning – ja</li>
              <li>♿ Bruser – ja</li>
              <li>♿ Skygge – ja</li>
            </ul>
          </div>
          <p>
            Det betyder, at man ikke kun har sørget for selve adgangen til havet. Der er også tænkt på vejen hen over stranden og de praktiske ting omkring en stranddag.
          </p>
          <p>
            Det er netop denne helhed, der gør Agioi Apostoloi særlig interessant.
          </p>
          <p>
            Området ligger kun få kilometer fra Chania, og her finder man også Aksos Suites, som er indrettet med særligt fokus på gæster med handicap.
          </p>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-bold text-slate-900">⭐ Marathi – Chania</h4>
          <p>
            Marathi ligger øst for Chania og er endnu en rigtig interessant mulighed.
          </p>
          <p>
            Stranden ligger i en beskyttet bugt, og SEATRACs officielle oplysninger viser en meget omfattende tilgængelighed.
          </p>
          <div>
            <p className="font-semibold text-slate-800 mb-2">Dokumenterede faciliteter:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
              <li>♿ SEATRAC – ja</li>
              <li>♿ Strandsti – ja</li>
              <li>♿ Parkering – ja</li>
              <li>♿ Toilet – ja</li>
              <li>♿ Omklædning – ja</li>
              <li>♿ Bruser – ja</li>
              <li>♿ Skygge – ja</li>
            </ul>
          </div>
          <p>
            Der er desuden omtale af livredder på stranden, hvilket kan give ekstra tryghed.
          </p>
          <p>
            Toiletterne ligger ifølge oplysninger fra en bruger af SEATRAC ved restauranterne.
          </p>
          <p>
            Marathi kan derfor være rigtig interessant som udflugtsstrand, hvis man bor i eller omkring Chania.
          </p>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-bold text-slate-900">⭐ Agios Onoufrios – Chania</h4>
          <p>
            Agios Onoufrios ligger nordøst for Chania og har også en meget komplet løsning.
          </p>
          <div>
            <p className="font-semibold text-slate-800 mb-2">Dokumenterede faciliteter:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
              <li>♿ SEATRAC – ja</li>
              <li>♿ Strandsti – ja</li>
              <li>♿ Parkering – ja</li>
              <li>♿ Toilet – ja</li>
              <li>♿ Omklædning – ja</li>
              <li>♿ Bruser – ja</li>
              <li>♿ Skygge – ja</li>
            </ul>
          </div>
          <p>
            Det er endnu et godt eksempel på, at betegnelsen “handicapvenlig strand” kan dække over meget mere end blot en kørestolsrampe.
          </p>
          <p>
            Her er der mulighed for både at komme hen over stranden og videre ud i vandet.
          </p>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-bold text-slate-900">⭐ Almyrida – Apokoronas</h4>
          <p>
            Almyrida ligger øst for Chania og er en mindre ferieby, som kan være interessant, hvis man gerne vil bo lidt roligere end inde i Chania.
          </p>
          <p>
            Også her viser SEATRACs officielle registrering en komplet række faciliteter.
          </p>
          <div>
            <p className="font-semibold text-slate-800 mb-2">Dokumenterede faciliteter:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
              <li>♿ SEATRAC – ja</li>
              <li>♿ Strandsti – ja</li>
              <li>♿ Parkering – ja</li>
              <li>♿ Toilet – ja</li>
              <li>♿ Omklædning – ja</li>
              <li>♿ Bruser – ja</li>
              <li>♿ Skygge – ja</li>
            </ul>
          </div>
          <p>
            Almyrida er derfor et af de områder, jeg bestemt ville kigge nærmere på, hvis ønsket er en ferieby med adgang til en tilgængelig strand.
          </p>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-bold text-slate-900">⭐ Kalyves – Apokoronas</h4>
          <p>
            Ikke langt fra Almyrida ligger Kalyves.
          </p>
          <p>
            Kalyves er ligeledes registreret som SEATRAC-strand og er interessant, fordi man kan kombinere en mindre kretensisk ferieby med bedre adgang til stranden.
          </p>
          <p>
            SEATRACs register viser strandfaciliteter på stedet, herunder adgangsløsninger omkring anlægget.
          </p>
          <p>
            Som ved alle strande anbefaler jeg, at den aktuelle status kontrolleres kort før rejsen.
          </p>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-bold text-slate-900">⭐ Rethymnon bystrand</h4>
          <p>
            Vil man gerne kombinere strand med en større by, er Rethymnon særlig interessant.
          </p>
          <p>
            På Rethymnons lange bystrand findes SEATRAC, og her er der registreret:
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
            <li>♿ SEATRAC – ja</li>
            <li>♿ Strandsti – ja</li>
            <li>♿ Parkering – ja</li>
            <li>♿ Toilet – ja</li>
            <li>♿ Omklædning – ja</li>
            <li>♿ Bruser – ja</li>
            <li>♿ Skygge – ja</li>
          </ul>
          <p>
            Det er en rigtig interessant kombination, fordi man samtidig kan bo i eller tæt ved Rethymnon og have restauranter, butikker og byliv i nærheden.
          </p>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-bold text-slate-900">⭐ Gerani – Rethymnon</h4>
          <p>
            Ved Gerani vest for Rethymnon findes endnu en meget komplet løsning.
          </p>
          <div>
            <p className="font-semibold text-slate-800 mb-2">Dokumenterede faciliteter:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
              <li>♿ SEATRAC – ja</li>
              <li>♿ Strandsti – ja</li>
              <li>♿ Parkering – ja</li>
              <li>♿ Toilet – ja</li>
              <li>♿ Omklædning – ja</li>
              <li>♿ Bruser – ja</li>
              <li>♿ Skygge – ja</li>
            </ul>
          </div>
          <p>
            SEATRACs fjernbetjening er registreret ved Fysalida Tavern.
          </p>
          <p>
            Det er en lille detalje, men netop den slags information kan være rigtig værdifuld, når man rent faktisk står på stranden og skal bruge systemet.
          </p>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-bold text-slate-900">⭐ Agia Galini – Sydkreta</h4>
          <p>
            Vil man hellere opleve Kretas sydkyst, er Agia Galini værd at kigge på.
          </p>
          <p>
            Her findes også en meget omfattende SEATRAC-løsning.
          </p>
          <div>
            <p className="font-semibold text-slate-800 mb-2">Dokumenterede faciliteter:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
              <li>♿ SEATRAC – ja</li>
              <li>♿ Strandsti – ja</li>
              <li>♿ Parkering – ja</li>
              <li>♿ Toilet – ja</li>
              <li>♿ Omklædning – ja</li>
              <li>♿ Bruser – ja</li>
              <li>♿ Skygge – ja</li>
            </ul>
          </div>
          <p>
            Der er registreret flere lokale tavernaer og en café som kontaktsteder for SEATRAC.
          </p>
          <p>
            Agia Galini viser derfor også, at man ikke behøver vælge de store turistområder på Kretas nordkyst for at få mulighed for en tilgængelig strand.
          </p>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-bold text-slate-900">⭐ Ligaria – nær Heraklion</h4>
          <p>
            Ligaria ligger i en smuk bugt vest for Heraklion.
          </p>
          <p>
            Her finder vi igen stort set hele pakken.
          </p>
          <div>
            <p className="font-semibold text-slate-800 mb-2">Dokumenterede faciliteter:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
              <li>♿ SEATRAC – ja</li>
              <li>♿ Strandsti – ja</li>
              <li>♿ Parkering – ja</li>
              <li>♿ Toilet – ja</li>
              <li>♿ Omklædning – ja</li>
              <li>♿ Bruser – ja</li>
              <li>♿ Skygge – ja</li>
              <li className="sm:col-span-2">🛟 Livredder – registreret som kontaktpunkt for SEATRAC</li>
            </ul>
          </div>
          <p>
            Det sidste er interessant, fordi SEATRACs officielle side angiver livredderen som stedet, hvor man får adgang til fjernbetjeningen.
          </p>
          <p>
            Ligaria kan derfor være en rigtig god strand at undersøge nærmere, hvis man bor omkring Heraklion eller på Kretas centrale nordkyst.
          </p>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-bold text-slate-900">⭐ Potamos – Hersonissos-området</h4>
          <p>
            Også i Hersonissos-området findes der muligheder.
          </p>
          <p>
            Ved Potamos er der registreret:
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
            <li>♿ SEATRAC – ja</li>
            <li>♿ Strandsti – ja</li>
            <li>♿ Parkering – ja</li>
            <li>♿ Toilet – ja</li>
            <li>♿ Omklædning – ja</li>
            <li>♿ Bruser – ja</li>
            <li>♿ Skygge – ja</li>
          </ul>
          <p>
            Der findes desuden registrerede SEATRAC-strande ved Agkisaras og Arina i samme del af Kreta.
          </p>
          <p>
            Det gør Hersonissos-området interessant, fordi der er flere muligheder inden for samme ferieområde.
          </p>
        </div>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Flere SEATRAC-strande på Kreta</h4>
        <p>
          SEATRACs officielle register indeholder endnu flere strande på Kreta.
        </p>
        <p>
          Blandt de registrerede finder man blandt andet:
        </p>
        <div className="bg-emerald-50/70 border border-emerald-100 p-5 rounded-xl space-y-3 text-sm">
          <div>
            <strong className="text-emerald-950 block mb-1">Chania og Vestkreta:</strong>
            <p className="text-emerald-900">Agioi Apostoloi 1 og 2, Marathi, Agios Onoufrios, Almyrida, Kalyves, Nea Chora og Kissamos.</p>
          </div>
          <div>
            <strong className="text-emerald-950 block mb-1">Rethymnon og Sydkreta:</strong>
            <p className="text-emerald-900">Rethymnon bystrand, Gerani, Agia Galini og Plakias.</p>
          </div>
          <div>
            <strong className="text-emerald-950 block mb-1">Heraklion/Hersonissos:</strong>
            <p className="text-emerald-900">Ligaria, Agkisaras, Arina og Potamos.</p>
          </div>
          <div>
            <strong className="text-emerald-950 block mb-1">Agios Nikolaos:</strong>
            <p className="text-emerald-900">Kitroplateia.</p>
          </div>
        </div>
        <p>
          Det er dog vigtigt at være opmærksom på, at et anlæg godt kan være registreret, selv om det midlertidigt er afmonteret eller ude af drift.
        </p>
        <p>
          Eksempelvis står Kitroplateia aktuelt registreret med parkering, strandsti, toilet, omklædning, skygge og bruser, men selve anlægget er markeret som afmonteret i SEATRACs register.
        </p>
        <p className="font-semibold text-slate-800">
          Derfor skal man aldrig vælge ferie alene ud fra en gammel liste over SEATRAC-strande.
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">SEATRAC er ikke det samme som strandhjælp</h4>
        <p>
          Det er også vigtigt at skelne mellem forskellige former for hjælp.
        </p>
        <p>
          På nogle strande findes SEATRAC, hvor formålet er at give en person med bevægelseshandicap mulighed for mere selvstændig adgang til havet.
        </p>
        <p>
          Andre steder kan der være strandkørestol og hjælp fra personale eller livreddere.
        </p>
        <p>
          En strandkørestol kræver typisk hjælp fra en anden person, mens SEATRAC er udviklet med selvstændig adgang til vandet som formål.
        </p>
        <p>
          Har du brug for fysisk hjælp til forflytning, bør det derfor undersøges særskilt. Man kan ikke gå ud fra, at der følger personlig assistance med, blot fordi en strand har SEATRAC.
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Hvad betyder “strandsti”?</h4>
        <p>
          Når SEATRAC angiver Beach Track, er der etableret fast adgang hen over stranden.
        </p>
        <p>
          Det er vigtigt, fordi en almindelig kørestol ofte ikke kan køre gennem løst sand.
        </p>
        <p>
          Men også her kan der være forskel på løsningerne. En sti kan eksempelvis føre til SEATRAC og de tilgængelige faciliteter uden nødvendigvis at betyde, at man frit kan køre med kørestol langs hele stranden.
        </p>
        <p>
          Det er derfor noget, jeg anbefaler at undersøge konkret, hvis man har særlige behov.
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Mine mest interessante områder</h4>
        <p>
          Hvis jeg skulle starte med at kigge efter en ferie på Kreta, hvor adgang til stranden og havet er højt prioriteret, ville jeg især undersøge:
        </p>
        <div className="space-y-3 bg-stone-50 p-6 rounded-2xl border border-stone-100 text-sm">
          <p><strong className="text-emerald-900">Agioi Apostoloi</strong> – meget omfattende faciliteter og to SEATRAC-anlæg.</p>
          <p><strong className="text-emerald-900">Almyrida/Kalyves</strong> – interessant hvis man ønsker en mindre ferieby.</p>
          <p><strong className="text-emerald-900">Rethymnon</strong> – god mulighed for at kombinere by og strand.</p>
          <p><strong className="text-emerald-900">Agia Galini</strong> – spændende mulighed på sydkysten.</p>
          <p><strong className="text-emerald-900">Ligaria/Hersonissos-området</strong> – flere tilgængelige strandmuligheder på den centrale del af nordkysten.</p>
        </div>
        <p className="italic text-slate-600">
          Men den bedste strand afhænger altid af den enkelte.
        </p>

        <div className="bg-amber-50/80 border border-amber-200/70 p-6 rounded-2xl space-y-3">
          <h4 className="text-lg font-bold text-amber-950">Handicapvenlig betyder ikke det samme for alle</h4>
          <p className="text-amber-900 font-medium">
            Det er måske det vigtigste budskab i hele denne guide.
          </p>
          <p className="text-amber-900">
            At en strand har SEATRAC, handicaptoilet og strandsti betyder ikke nødvendigvis, at den fungerer for dig.
          </p>
          <ul className="space-y-1.5 text-sm text-amber-900 pt-2">
            <li>• Kan du selv forflytte dig fra kørestolen?</li>
            <li>• Har du brug for hjælp?</li>
            <li>• Skal din kørestol kunne komme helt tæt på vandet?</li>
            <li>• Har du behov for et stort handicaptoilet?</li>
            <li>• Har du brug for handicapparkering meget tæt på stranden?</li>
            <li>• Og hvordan kommer du fra dit hotel til stranden?</li>
          </ul>
          <p className="text-amber-900 pt-2 text-sm">
            Det er spørgsmål, der kan være mindst lige så vigtige som selve SEATRAC-anlægget.
          </p>
        </div>

        <div className="bg-emerald-900 text-white p-6 md:p-8 rounded-2xl space-y-4">
          <h4 className="font-serif font-bold text-xl text-emerald-100">Derfor ser jeg på hele ferien</h4>
          <p className="text-emerald-50">
            Hos Alora Rejser handler en handicapvenlig ferie ikke kun om at finde et hotel med et kørestolssymbol.
          </p>
          <p className="text-emerald-50 font-medium">
            Jeg ser på hele rejsen.
          </p>
          <p className="text-emerald-100 text-sm">
            Hotellet, badeværelset, pladsen omkring sengen, poolen, området omkring hotellet, transporten – og selvfølgelig muligheden for at komme på stranden og i havet.
          </p>
          <p className="text-emerald-100 text-sm">
            For der er ikke meget ved at finde det perfekte handicapværelse, hvis stranden 300 meter derfra er utilgængelig.
          </p>
          <p className="text-emerald-100 text-sm">
            Og omvendt kan en fantastisk tilgængelig strand være svær at bruge, hvis vejen fra hotellet derned ikke fungerer med kørestol.
          </p>
          <p className="text-emerald-100 text-sm">
            Derfor er det vigtigt, at du fortæller mig om dine behov, når vi finder din ferie. Handicap og funktionsniveau er forskelligt fra person til person, og jo mere jeg ved, jo bedre kan jeg undersøge, hvilken løsning der passer til dig.
          </p>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 p-6 rounded-2xl space-y-3">
          <h4 className="text-base font-bold text-slate-800">Vigtigt – kontrollér altid status før rejsen</h4>
          <p className="text-sm text-slate-600">
            SEATRAC står tæt på havet og påvirkes af vejr og bølger. Et anlæg kan derfor blive taget midlertidigt ud af drift eller være afmonteret uden for sæsonen.
          </p>
          <p className="text-sm text-slate-600">
            Oplysninger om parkering, toilet, strandsti osv. fortæller desuden, at faciliteten er registreret ved stranden – ikke nødvendigvis præcise mål, hældninger eller om den vil fungere for enhver kørestol.
          </p>
          <p className="text-sm text-slate-600 font-medium">
            Hos Alora Rejser undersøger jeg derfor de konkrete forhold ud fra den enkelte rejsendes behov.
          </p>
        </div>

        <p className="pt-4 text-center font-serif italic text-lg text-emerald-800 font-bold">
          Ferie skal føles som ferie – for alle
        </p>
      </div>
    )
  },
  {
    id: 'hvad-skal-du-huske-paa-ferie-som-koerestolsbruger',
    title: 'Hvad skal du huske på ferie som kørestolsbruger?',
    date: 'juni 2026',
    category: 'Praktisk info',
    categories: ['Praktisk info'],
    intro: 'At planlægge en ferie som kørestolsbruger kræver ofte lidt ekstra forberedelse.',
    image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/rejsetips-hero.jpg',
    imageLarge: 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/rejsetips-hero.jpg',
    routePath: '/da/rejsetips/hvad-skal-du-huske-paa-ferie-som-koerestolsbruger',
    content: <RejsetipsGuideContent />
  },
  {
    id: 'inspektionsuge-mallorca',
    title: '🌴 Min inspektionsuge på Mallorca: 40+ hoteller besøgt personligt 🏖️☀️💚',
    date: 'august 2026',
    category: 'Hotelinspektioner',
    categories: ['Hotelinspektioner', 'Handicaprejser', 'Destinationer', 'Praktisk info'],
    intro: 'Så er jeg hjemme igen efter en fantastisk – og meget travl – uge på Mallorca med besøg på over 40 hoteller. Læs hele beretningen fra min første store inspektionstur.',
    image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/image_2fbecbb8.png',
    imageLarge: 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/image_2fbecbb8.png',
    content: (
      <div className="space-y-6 text-slate-700 leading-relaxed">
        <div className="bg-emerald-50/80 border border-emerald-200/80 p-6 rounded-2xl">
          <p className="text-lg font-serif font-bold text-emerald-950 mb-1">
            🌴 Så er jeg hjemme igen efter en fantastisk – og meget travl – uge på Mallorca. 🏖️☀️💚
          </p>
          <p className="text-sm text-emerald-800">
            En personlig beretning om Alora Rejsers første store inspektionstur
          </p>
        </div>

        <p>
          Den sidste uge har budt på lange dage, mange timer på vejene og besøg på mere end 40 hoteller rundt omkring på øen🏨
        </p>

        <p>
          Jeg har været hele vejen rundt – fra Palma, Playa de Palma, Magaluf, Cala Millor til Sa Coma, Alcúdia, Playa de Muro, Can Picafort og Port de Pollença. Nogle dage har jeg kørt fra den ene ende af øen til den anden, men jeg har elsket hvert eneste minut😎
        </p>

        <div className="bg-stone-50 border border-stone-200/90 p-6 rounded-2xl space-y-4">
          <h4 className="font-serif font-bold text-slate-900 text-lg">
            Hvorfor personlige hotelbesøg?
          </h4>
          <p>
            Det her var jo min allerførste inspektionstur, hvor formålet var at besøge hoteller, for at se om de levede op til min standart og for at finde nye fantastiske steder🙏🏼
          </p>
          <p>
            Derfor var jeg også lidt spændt hjemmefra!
            Hvordan ville hotellerne tage imod mig? Ville de have tid til mig? Ville de forstå, hvorfor jeg stiller så mange spørgsmål🙈
          </p>
          <p>
            Heldigvis blev alle mine bekymringer gjort til skamme🤗
          </p>
          <p>
            Jeg er blevet mødt med en utrolig varme og hjælpsomhed overalt🙏🏼
            Personalet har været fantastisk, vist mig rundt, svaret tålmodigt på mine mange spørgsmål og gjort alt, hvad de kunne, for at give mig så meget viden som muligt.
            Det er jeg virkelig taknemmelig for❤️
          </p>
        </div>

        <div className="bg-amber-50/70 border border-amber-200/80 p-6 rounded-2xl space-y-3">
          <h4 className="font-serif font-bold text-amber-950 text-lg">
            Mindre brochure, mere virkelighed
          </h4>
          <p>
            Flere har spurgt mig, hvorfor jeg bruger så meget tid på at besøge hotellerne personligt, når jeg kan læse om dem på nettet, det må være nok her i opstartsfasen, og ja det er det måske for nogen🤔
          </p>
          <p className="font-medium text-slate-900">
            For mig er et hotel meget mere end flotte billeder og en god beskrivelse og jeg har virkelig brug for at kunne stå inde for hver en rejse jeg sælger🙏🏼❤️
          </p>
          <p>
            Jeg vil mærke stemningen. Se, hvordan personalet tager imod gæsterne. Gå rundt på området, opleve værelserne og undersøge, om tilgængeligheden rent faktisk fungerer i virkeligheden – ikke bare på papiret!
          </p>
        </div>

        <p>
          Når man lever tæt på et handicap i hverdagen, lægger man mærke til nogle helt andre ting end de fleste. Det er netop de detaljer, jeg leder efter. Er elevatoren stor nok? Er poolen nem at komme i? Hvordan fungerer badeværelset? Er adgangsforholdene gode? Det er de små ting, der kan gøre den store forskel – og som brochurer og flotte billeder sjældent fortæller hele historien om.
        </p>

        <p className="font-medium text-emerald-900 bg-emerald-50/50 p-4 rounded-xl border border-emerald-100">
          Det er først, når jeg selv har været der, at jeg kan anbefale et hotel med ro i maven.
        </p>

        <p>
          Og det er også derfor, at jeg faktisk har valgt flere hoteller fra på denne tur. De så flotte ud hjemmefra, men levede ikke op til de krav, jeg stiller. Omvendt fandt jeg også nogle helt fantastiske steder, som overraskede mig meget positivt🤗😎
        </p>

        <p>
          Denne gang har jeg også været på jagt efter noget lidt anderledes. Små boutiquehoteller, charmerende fincaer og steder med masser af sjæl – steder, som skiller sig ud fra de klassiske resorts.
        </p>

        <p>
          Det er netop den slags oplevelser, jeg selv har savnet at kunne finde til min mand og jeg, samtidig med de stadig er gode tilgængelige🤗
        </p>

        <div className="bg-stone-50 p-6 rounded-2xl border border-stone-200 space-y-3">
          <p>
            Nu venter det hyggelige arbejde med at gennemgå alle billederne, videoerne og mine noter. De kommende dage vil jeg begynde at dele de hoteller, jeg har besøgt, og fortælle ærligt om mine oplevelser – både de steder, jeg allerede samarbejder med, og måske også nogle helt nye destinationer, som jeg glæder mig til at præsentere.
          </p>
          <p className="font-semibold text-emerald-950">
            Tusind tak fordi I følger med og støtter Alora Rejser. 💚 Det betyder mere, end I aner. Jeg glæder mig til at tage jer med rundt på Mallorca – hotel for hotel.
          </p>
        </div>
      </div>
    )
  },
  {
    id: 'midtjyllands-avis-omtale',
    title: 'En stor milepæl for Alora Rejser! 💚 Omtale i Midtjyllands Avis',
    date: 'august 2026',
    category: 'Nyheder & presse',
    categories: ['Nyheder & presse', 'Praktisk info', 'Handicaprejser'],
    intro: 'Vi er utrolig stolte over, at Alora Rejser er blevet omtalt i Midtjyllands Avis! Artiklen fortæller historien bag Alora Rejser, og hvorfor vi brænder for at gøre det lettere at komme trygt på ferie.',
    image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/midtjyllands-avis-omtale.jpg',
    imageLarge: 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/midtjyllands-avis-omtale.jpg',
    content: (
      <div className="space-y-6 text-slate-700 leading-relaxed">
        <div className="bg-emerald-50/80 border border-emerald-200/80 p-6 rounded-2xl">
          <div className="flex items-center gap-2 text-emerald-800 font-bold text-sm mb-1">
            <Award size={18} />
            <span>Presse & omtale</span>
          </div>
          <h3 className="text-xl md:text-2xl font-serif font-bold text-emerald-950">
            En stor milepæl for Alora Rejser! 💚
          </h3>
        </div>

        <p className="text-base sm:text-lg font-medium text-slate-900">
          Vi er utrolig stolte over, at Alora Rejser er blevet omtalt i Midtjyllands Avis🙏🏼
        </p>

        <p>
          Artiklen fortæller historien bag Alora Rejser – og hvorfor vi brænder for at gøre det lettere for mennesker med handicap og deres familier at komme trygt på ferie❤️
        </p>

        <div className="bg-stone-50 border border-stone-200/80 p-6 rounded-2xl space-y-3">
          <p>
            Bag Alora Rejser ligger mange timers arbejde, stor dedikation og et ønske om at skabe noget, der gør en reel forskel🤗
          </p>
          <p>
            Vi besøger selv hoteller og destinationer, så vi kan give ærlig og grundig rådgivning om tilgængelighed – fordi detaljerne betyder alt🙏🏼❤️🌴
          </p>
        </div>

        <p>
          Tak til Midtjyllands Avis for at fortælle vores historie, og tak til alle jer, der følger med og støtter Alora Rejser🥳
          Det giver os endnu mere lyst til at fortsætte arbejdet med at finde de bedste og mest tilgængelige ferieoplevelser verden over🌍✈️
        </p>

        <p className="font-medium text-slate-900">
          Vi glæder os til at hjælpe endnu flere med at rejse – uden besvær😍🏖️🌴
        </p>

        <div className="pt-4">
          <a
            href="https://midtjyllandsavis.dk/artikel/76ece63d-ddc3-4233-aa96-d3c3961b716f?fbclid=IwdGRjcATpJL1wZG9mAWZkaWQWUMXXO7NLC20sk4GP_D9caHnKPg4dImV4dG4DYWVtAjExAHNydGMGYXBwX2lkCjY2Mjg1NjgzNzkAAR7iPM2a59UaCyGErV2yX08Mo6W6Fl006jYMj6nCh6US1_DphVK2Azp8yVlVpA_aem_IFNN7Qt_ye-MKDXnGuW8rg"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-emerald-800 text-white font-medium px-6 py-3 rounded-full hover:bg-emerald-900 transition-colors shadow-sm"
          >
            <span>Læs artiklen hos Midtjyllands Avis</span>
            <ExternalLink size={16} />
          </a>
        </div>
      </div>
    )
  }
];

// Artikel-data (English)
export const articles_en = [
  {
    id: 'invisible-disabilities-badge-spanien',
    title: 'New rule in Spanish airports: The Sunflower Lanyard is no longer enough',
    date: 'May 2025',
    category: 'Airport & assistance',
    categories: ['Airport & assistance', 'Practical info'],
    intro: 'Aena airports in Spain have introduced a free "Invisible Disabilities Badge". Read what this means for you and how to apply before your trip.',
    image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/invisible-disabilities-airport.jpg',
    imageLarge: 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/invisible-disabilities-airport.jpg',
    content: (
      <div className="space-y-6 text-slate-700 leading-relaxed">
        <h3 className="text-xl md:text-2xl font-serif font-bold text-slate-800 border-b border-emerald-100 pb-2">
          Important information for travelers with invisible disabilities in Spain
        </h3>
        <p>
          Are you traveling through Palma de Mallorca or another Spanish Aena airport, and do you or one of your fellow travelers have an invisible disability such as autism, ADHD, anxiety, PTSD, dementia, or similar?
        </p>
        <p>
          Then there is an important change that many do not yet know about.
        </p>
        <p>
          The well-known Sunflower Lanyard, also known as the Hidden Disabilities Sunflower Lanyard, no longer grants access to Aena's special security lanes in participating Spanish airports.
        </p>
        <p>
          Instead, Aena has introduced their own free Invisible Disabilities Badge, which must be applied for online before your trip.
        </p>

        <div className="bg-emerald-50 border border-emerald-100 p-6 rounded-2xl my-6">
          <h4 className="font-bold text-emerald-800 mb-3">The badge can:</h4>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Make it easier for airport staff to identify passengers who may need extra support</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Grant access to special security lanes where available</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Be shown on a mobile phone or printed</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Be used by a family or travel group, as only one badge is needed per family or travel group</span>
            </li>
          </ul>
        </div>

        <div className="bg-amber-50 border border-amber-100 p-6 rounded-2xl my-6">
          <h4 className="font-bold text-amber-800 mb-3">Important to know</h4>
          <ul className="space-y-2 text-amber-900">
            <li className="flex items-start gap-2">
              <span className="font-bold text-amber-600 mr-1">•</span>
              <span>The badge is not the same as Fast Track</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-amber-600 mr-1">•</span>
              <span>It does not exempt you from security screening</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-amber-600 mr-1">•</span>
              <span>It does not replace pre-booked airport assistance</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-amber-600 mr-1">•</span>
              <span>It is only valid on the day of travel</span>
            </li>
          </ul>
        </div>

        <h4 className="font-bold text-slate-800 mt-6">Airports where the scheme is available include</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 my-2">
          {['Palma de Mallorca', 'Alicante', 'Barcelona', 'Málaga', 'Ibiza', 'Menorca', 'Lanzarote', 'Fuerteventura', 'Gran Canaria', 'Tenerife South'].map((airport) => (
            <div key={airport} className="bg-stone-50 px-3 py-2 rounded-lg border border-stone-100 text-sm font-medium text-slate-700">
              {airport}
            </div>
          ))}
        </div>

        <p className="text-sm text-slate-500 italic mt-4">
          Applications should be made at least 48 hours before departure.
        </p>

        <div className="pt-4">
          <a
            href="https://www.aena.es/en/passengers/travellers/passengers-with-special-needs/invisible-disabilities/invisible-disabilities-badge.html"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#99A18C] text-white px-6 py-3.5 rounded-xl font-bold hover:bg-[#8a917e] transition-all shadow-md hover:shadow-lg"
          >
            Read more and apply at Aena
            <ExternalLink size={16} />
          </a>
        </div>

        <p className="pt-6 border-t border-slate-100 italic text-slate-600">
          At Alora Rejser, we know that peace of mind on your journey means a lot. That is why we are happy to share information that can make traveling a little easier for people with both visible and invisible disabilities.
        </p>
      </div>
    )
  },
  {
    id: 'disneyland-paris-i-koerestol',
    title: 'Disney in a wheelchair?',
    date: 'June 2025',
    category: 'Accessible travel',
    categories: ['Accessible travel', 'Destinations'],
    intro: 'Disneyland Paris is among the most accessible theme parks in Europe. Get an overview of the Priority Card, discounts, wheelchair rental, hotels, and transport.',
    image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/disneyland-wheelchair.jpg',
    imageLarge: 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/disneyland-wheelchair.jpg',
    content: (
      <div className="space-y-6 text-slate-700 leading-relaxed">
        <h3 className="text-xl md:text-2xl font-serif font-bold text-slate-800 border-b border-emerald-100 pb-2">
          Disney in a wheelchair?
        </h3>
        <p>
          A visit to Disneyland Paris can be a fantastic experience for wheelchair users. The parks are among the most accessible in Europe and offer solutions that make it easier to experience rides, shows, parades, and restaurants.
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Priority Card – the most important thing to know</h4>
        <p>
          If you have a disability or reduced mobility, it is highly recommended to apply for a Priority Card before your visit.
        </p>
        <div className="bg-emerald-50 border border-emerald-100 p-5 rounded-xl">
          <p className="font-semibold text-emerald-800 mb-2">The card gives priority access to:</p>
          <ul className="grid grid-cols-2 gap-2 text-sm text-emerald-900">
            <li className="flex items-center gap-2">✔ Rides</li>
            <li className="flex items-center gap-2">✔ Shows</li>
            <li className="flex items-center gap-2">✔ Parades</li>
            <li className="flex items-center gap-2">✔ Character Meets</li>
            <li className="flex items-center gap-2">✔ Selected Areas</li>
          </ul>
        </div>
        <p className="text-sm">
          Application is done online:{' '}
          <a
            href="https://www.disneylandparis.com/en-int/guest-services/priority-card/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#99A18C] font-semibold underline hover:text-[#8a917e]"
          >
            Apply here <ExternalLink size={12} className="inline" />
          </a>
          <br />
          Upon arrival, you must bring valid documentation of your disability.
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Discounts on park tickets</h4>
        <p>
          Guests with disabilities can often get a discount on park tickets for themselves and one companion.
          <br />
          <a
            href="https://tickets.disneylandparis.com/en-gb/tickets-guests-with-disabilities"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#99A18C] font-semibold underline hover:text-[#8a917e]"
          >
            Read more about discounts here <ExternalLink size={12} className="inline" />
          </a>
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Wheelchair rental in the park</h4>
        <p>
          Wheelchairs can be rented directly in the parks. Rental locations are at:
        </p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li>Town Square Terrace in Disneyland Park</li>
          <li>Studio Services in Walt Disney Studios Park</li>
        </ul>
        <p>
          Rentals are on a first-come, first-served basis, and a small daily fee plus a refundable deposit is required.
          <br />
          <a
            href="https://www.disneylandparis.com/en-int/guest-services/accessibility-at-disneylandparis/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#99A18C] font-semibold underline hover:text-[#8a917e]"
          >
            Read more about accessibility here <ExternalLink size={12} className="inline" />
          </a>
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Attractions and accessibility</h4>
        <p>
          It varies from attraction to attraction whether you can remain in your wheelchair or if transfer is required. Therefore, we always recommend checking the current accessibility with Disneyland Paris before your visit.
        </p>

        <div className="space-y-6">
          {/* Attractions where many wheelchair users can participate */}
          <div className="bg-emerald-50/50 border border-emerald-100 p-6 rounded-2xl">
            <h5 className="font-serif font-bold text-emerald-900 text-base mb-2">
              Attractions where many wheelchair users can participate
            </h5>
            <p className="text-xs text-emerald-800/80 mb-4">
              Here you can often participate directly in your wheelchair or with minimal transfer.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h6 className="font-bold text-xs text-emerald-800 uppercase tracking-wider mb-2 border-b border-emerald-100 pb-1">
                  Disneyland Park
                </h6>
                <ul className="text-sm space-y-1.5 text-slate-700">
                  <li>• It’s a Small World</li>
                  <li>• Pirates of the Caribbean</li>
                  <li>• Phantom Manor</li>
                  <li>• Buzz Lightyear Laser Blast</li>
                  <li>• Mickey’s PhilharMagic</li>
                  <li>• Le Passage Enchanté d’Aladdin</li>
                  <li>• La Tanière du Dragon</li>
                  <li>• Les Mystères du Nautilus</li>
                  <li>• Thunder Mesa Riverboat Landing</li>
                  <li>• Disneyland Railroad, selected stations</li>
                </ul>
              </div>
              <div>
                <h6 className="font-bold text-xs text-emerald-800 uppercase tracking-wider mb-2 border-b border-emerald-100 pb-1">
                  Walt Disney Studios Park / Disney Adventure World
                </h6>
                <ul className="text-sm space-y-1.5 text-slate-700">
                  <li>• Ratatouille: The Adventure</li>
                  <li>• Cars ROAD TRIP</li>
                  <li>• Spider-Man W.E.B. Adventure</li>
                  <li>• Animation Academy</li>
                  <li>• Disney Junior Dream Factory</li>
                  <li>• Mickey and the Magician</li>
                  <li>• Together: A Pixar Musical Adventure</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Attractions that normally require transfer */}
          <div className="bg-amber-50/40 border border-amber-100 p-6 rounded-2xl">
            <h5 className="font-serif font-bold text-amber-900 text-base mb-2">
              Attractions that normally require transfer from the wheelchair
            </h5>
            <p className="text-xs text-amber-800/80 mb-4">
              For these rides, you must be able to transfer to the ride's seat (either by yourself or with the help of a companion).
            </p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-slate-700">
              <li>• Big Thunder Mountain</li>
              <li>• Peter Pan’s Flight</li>
              <li>• Dumbo the Flying Elephant</li>
              <li>• Star Wars Hyperspace Mountain</li>
              <li>• Orbitron</li>
              <li>• Mad Hatter’s Tea Cups</li>
              <li>• Indiana Jones and the Temple of Peril</li>
              <li>• Star Tours</li>
              <li>• RC Racer</li>
              <li>• Crush’s Coaster</li>
              <li>• The Twilight Zone Tower of Terror</li>
            </ul>
          </div>

          {/* Shows and experiences with good accessibility */}
          <div className="bg-stone-50 border border-stone-200 p-6 rounded-2xl">
            <h5 className="font-serif font-bold text-slate-900 text-base mb-2">
              Shows and experiences with good accessibility
            </h5>
            <p className="text-xs text-slate-500 mb-4">
              These experiences have dedicated wheelchair spaces and excellent access conditions.
            </p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-slate-700">
              <li>• Disney Stars on Parade</li>
              <li>• Evening Fireworks & Drone Show</li>
              <li>• Character Meets</li>
              <li>• Mickey and the Magician</li>
              <li>• The Lion King: Rhythms of the Pride Lands</li>
              <li>• Together: A Pixar Musical Adventure</li>
              <li>• Disney Junior Dream Factory</li>
            </ul>
          </div>
        </div>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Restaurants and shops</h4>
        <p>
          Most restaurants and shops have step-free access and wide doors. For table-service restaurants, we recommend booking a table in advance via the Disneyland Paris app.
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Transport and accessible hotels</h4>
        <p>
          Official Disney hotels offer accessible rooms with adapted bathrooms. The free shuttle buses between the hotels and the parks are equipped with wheelchair ramps.
          <br />
          If you take the train from Paris, RER A is the easiest option. The Marne-la-Vallée/Chessy station is located right at the park entrance.
        </p>

        <div className="bg-emerald-900 text-white p-6 rounded-2xl mt-8">
          <h4 className="font-serif font-bold text-xl mb-3 text-emerald-100 italic">Alora Rejser's Tip</h4>
          <p className="text-emerald-50 mb-4">
            Disneyland Paris is one of the destinations in Europe where accessibility generally works very well for wheelchair users.
          </p>
          <p className="font-semibold text-emerald-200 mb-2">Our best advice is to:</p>
          <ul className="space-y-1 text-sm text-emerald-100">
            <li>• Apply for a Priority Card from home</li>
            <li>• Bring documentation</li>
            <li>• Book restaurants well in advance</li>
            <li>• Check the attractions' access requirements beforehand</li>
          </ul>
        </div>
      </div>
    )
  },
  {
    id: 'handicapvenlige-strande-paa-mallorca',
    title: 'Accessible beaches in Mallorca',
    date: 'June 2025',
    category: 'Destinations',
    categories: ['Destinations', 'Accessible travel', 'Practical info'],
    intro: 'Mallorca has several beaches with ramps, accessible toilets, beach wheelchairs, and beach assistance. Get an overview of the best areas.',
    image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/378a20d8-af7c-4ed6-9273-84c31896612e.jpg',
    imageLarge: 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/378a20d8-af7c-4ed6-9273-84c31896612e.jpg',
    content: (
      <div className="space-y-6 text-slate-700 leading-relaxed">
        <h3 className="text-xl md:text-2xl font-serif font-bold text-slate-800 border-b border-emerald-100 pb-2">
          Accessible beaches in Mallorca
        </h3>
        <p>
          Mallorca is one of the most accessible destinations in Southern Europe. Several of the island's beaches offer ramps, accessible toilets, beach wheelchairs, and beach assistance, so that people with reduced mobility can also enjoy a day by the sea.
        </p>
        <p>
          At Alora Rejser, we know that a beach is not necessarily accessible just because there is a promenade. That is why we also look at access to the water, toilet conditions, terrain, and options for assistance on the beach.
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">What is beach assistance?</h4>
        <p>
          Beach assistance is a free service offered by several municipalities in Mallorca during the summer months.
        </p>
        <div className="bg-emerald-50 border border-emerald-100 p-5 rounded-xl">
          <p className="font-semibold text-emerald-800 mb-2">Here you can typically get help from trained staff to:</p>
          <ul className="space-y-1.5 text-sm text-emerald-900">
            <li>• Get from the promenade down to the beach</li>
            <li>• Use a beach wheelchair, also known as an amphibious chair</li>
            <li>• Get safely into and out of the water</li>
            <li>• Practical help in the beach area</li>
          </ul>
        </div>
        <p>
          On most beaches, there are also accessible toilets and ramps close to the beach assistance area. The facilities are normally available from June to September, but opening hours and locations can vary from year to year.
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Alcúdia Beach</h4>
        <p>
          Alcúdia is one of the best beaches in Mallorca for wheelchair users. The wide sandy beach combined with flat terrain makes the area highly accessible.
        </p>
        <ul className="grid grid-cols-2 gap-2 text-sm bg-stone-50 p-4 rounded-xl border border-stone-100">
          <li>✔ Accessible toilets</li>
          <li>✔ Wooden ramps towards the water</li>
          <li>✔ Accessible parking</li>
          <li>✔ Beach assistance in the summer season</li>
          <li>✔ Beach wheelchairs</li>
          <li>✔ Calm and shallow water</li>
        </ul>
        <p>
          The most accessible areas are usually found at the central beach sections near the harbor and the promenade. Alcúdia is particularly suitable for families with children.
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Port de Pollença</h4>
        <p>
          Port de Pollença is one of our personal favorite areas in Mallorca. The long promenade makes it easy to get around, and the area generally has very few hills.
        </p>
        <ul className="grid grid-cols-2 gap-2 text-sm bg-stone-50 p-4 rounded-xl border border-stone-100">
          <li>✔ Ramps to the beach</li>
          <li>✔ Accessible toilets in several places along the promenade</li>
          <li>✔ Flat terrain</li>
          <li>✔ Good access to restaurants and cafes</li>
          <li>✔ Accessible walking areas along the sea</li>
          <li>✔ Beach assistance in the summer period</li>
        </ul>
        <p>
          With the addition of beach assistance in the summer period, Port de Pollença is now on par with the best areas in Mallorca and is one of the absolute easiest places to vacation as a wheelchair user.
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Cala Millor</h4>
        <p>
          Cala Millor has been developed over many years with a focus on accessibility. The wide promenade and numerous facilities make the area popular with both families and adults.
        </p>
        <ul className="grid grid-cols-2 gap-2 text-sm bg-stone-50 p-4 rounded-xl border border-stone-100">
          <li>✔ Accessible toilets</li>
          <li>✔ Ramps to the beach</li>
          <li>✔ Beach assistance in the summer season</li>
          <li>✔ Beach wheelchairs</li>
          <li>✔ Flat promenade along the entire beach</li>
          <li>✔ Accessible parking</li>
        </ul>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Sa Coma</h4>
        <p>
          Sa Coma is located close to Cala Millor and also offers good conditions for wheelchair users.
        </p>
        <ul className="grid grid-cols-2 gap-2 text-sm bg-stone-50 p-4 rounded-xl border border-stone-100">
          <li>✔ Accessible toilets</li>
          <li>✔ Ramps to the beach</li>
          <li>✔ Beach assistance in the summer season</li>
          <li>✔ Beach wheelchairs</li>
          <li>✔ Flat terrain around the promenade</li>
          <li>✔ Good access to shops</li>
        </ul>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Playa de Palma</h4>
        <p>
          Playa de Palma is located just a few kilometers from the airport and is therefore a popular choice for many. The long beach promenade makes the area easy to get around.
        </p>
        <ul className="grid grid-cols-2 gap-2 text-sm bg-stone-50 p-4 rounded-xl border border-stone-100">
          <li>✔ Accessible toilets</li>
          <li>✔ Ramps to the beach</li>
          <li>✔ Flat terrain</li>
          <li>✔ Accessible restaurants</li>
          <li>✔ Beach assistance in selected sections</li>
        </ul>

        <h4 className="text-lg font-bold text-slate-800 mt-6">Which area should you choose?</h4>
        <div className="space-y-6 bg-stone-50 p-6 rounded-2xl border border-stone-100">
          <div>
            <h5 className="font-bold text-emerald-900 text-base mb-1">Port de Pollença</h5>
            <p className="text-sm text-slate-600 leading-relaxed">
              If you want a cozy, authentic holiday town with a flat promenade, quiet surroundings, and good access between hotel, beach, restaurants, and shops, Port de Pollença is an excellent choice.
            </p>
          </div>
          <div className="border-t border-stone-200/60 pt-4">
            <h5 className="font-bold text-emerald-900 text-base mb-1">Sa Coma</h5>
            <p className="text-sm text-slate-600 leading-relaxed">
              If you want a quieter town with a wide sandy beach, a long beach promenade, and good accessible facilities close to the hotels, Sa Coma is a popular choice.
            </p>
          </div>
          <div className="border-t border-stone-200/60 pt-4">
            <h5 className="font-bold text-emerald-900 text-base mb-1">Cala Millor</h5>
            <p className="text-sm text-slate-600 leading-relaxed">
              If you want a lively holiday area with many restaurants, shops, and a long, accessible beach promenade, Cala Millor is a great choice.
            </p>
          </div>
          <div className="border-t border-stone-200/60 pt-4">
            <h5 className="font-bold text-emerald-900 text-base mb-1">Palma Coast / Playa de Palma / Can Pastilla</h5>
            <p className="text-sm text-slate-600 leading-relaxed">
              If you want a short transfer from the airport, city and sights close by, a long beach promenade, and easy access to Palma city, the Palma Coast is an obvious choice.
            </p>
          </div>
        </div>

        <div className="bg-emerald-900 text-white p-6 rounded-2xl mt-8">
          <h4 className="font-serif font-bold text-xl mb-3 text-emerald-100 italic">Alora Rejser's Tip</h4>
          <p className="text-emerald-50 mb-4">
            When we select hotels in Mallorca, we don't just look at the hotel. We also investigate:
          </p>
          <ul className="space-y-1 text-sm text-emerald-100">
            <li>• Distance to the beach</li>
            <li>• Whether there are stairs along the way</li>
            <li>• Location of accessible toilets</li>
            <li>• Availability of beach assistance</li>
            <li>• Beach wheelchairs and access to the sea</li>
            <li>• Accessible restaurants and shops in the local area</li>
          </ul>
          <p className="text-emerald-200 text-sm mt-4 font-medium">
            This way, you get a more realistic picture of how easy it actually is to vacation as a wheelchair user.
          </p>
        </div>

        <p className="pt-6 border-t border-slate-100 italic text-slate-600">
          If you have questions about accessible beaches or hotels in Mallorca, you are always welcome to contact Alora Rejser.
        </p>
      </div>
    )
  },
  {
    id: 'handicapvenlige-strande-paa-kreta',
    title: 'Accessible beaches in Crete – where can you get right down to the water in a wheelchair?',
    date: 'September 2025',
    category: 'Destinations',
    categories: ['Destinations', 'Accessible travel', 'Practical info'],
    intro: 'Crete has fantastic beaches, but for wheelchair users or those with walking difficulties, a beautiful beach is not necessarily usable. Get a guide to the best options with SEATRAC and beach tracks.',
    image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/seatrac-handicapvenlig-strand-kreta.jpg',
    imageLarge: 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/seatrac-handicapvenlig-strand-kreta.jpg',
    content: (
      <div className="space-y-6 text-slate-700 leading-relaxed">
        <h3 className="text-xl md:text-2xl font-serif font-bold text-slate-800 border-b border-emerald-100 pb-2">
          Accessible beaches in Crete – where can you get right down to the water in a wheelchair?
        </h3>
        <p>
          Crete has fantastic beaches, but if you use a wheelchair or have walking difficulties, a beautiful beach is not necessarily a beach you can use.
        </p>
        <p>
          The sand can be an almost impossible obstacle, and even just a few meters from a promenade to the water's edge can mean you can go no further.
        </p>
        <p>
          Fortunately, there are several places in Crete where accessibility has been thought through much better. On a number of beaches you will find SEATRAC, beach tracks across the sand, accessible toilets, parking, changing facilities, showers, and shaded areas.
        </p>
        <p>
          Here is a guide to some of the best options. I have included most of Crete; even though we do not have hotels everywhere, a day trip can also be a fantastic option⛱️ ❤️
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">First: What is SEATRAC?</h4>
        <p>
          SEATRAC is a system that allows people with reduced mobility to get from the beach directly into the sea.
        </p>
        <p>
          You sit on a seat that runs on a rail into the water. The system is designed so that in many cases the user can enter the sea more independently and without having to be carried through the sand.
        </p>
        <p>
          It is free of charge to use.
        </p>
        <p>
          However, it is important to distinguish between SEATRAC and a beach track.
        </p>
        <p>
          SEATRAC helps you that last stretch into the water itself, while a beach track or beach mat makes it possible to roll across the sand towards the water and the SEATRAC facility in a wheelchair.
        </p>
        <p>
          For many wheelchair users, the combination of the two is therefore crucial.
        </p>

        <div className="bg-stone-50 border border-stone-200/80 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-bold text-slate-900">⭐ Agioi Apostoloi – Chania</h4>
          <p>
            Agioi Apostoloi west of Chania is, in my assessment, one of the most interesting beaches to look at if accessibility is important for your holiday.
          </p>
          <p>
            Here you will find two SEATRAC facilities, and a wide range of accessible facilities are also registered.
          </p>
          <div>
            <p className="font-semibold text-slate-800 mb-2">Documented facilities:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
              <li>♿ SEATRAC – yes</li>
              <li>♿ Beach track over sand – yes</li>
              <li>♿ Parking – yes</li>
              <li>♿ Toilet – yes</li>
              <li>♿ Accessible changing room – yes</li>
              <li>♿ Shower – yes</li>
              <li>♿ Shade – yes</li>
            </ul>
          </div>
          <p>
            This means that not only has access to the sea been provided; thought has also gone into the path across the beach and the practical aspects of a beach day.
          </p>
          <p>
            It is precisely this comprehensive approach that makes Agioi Apostoloi particularly interesting.
          </p>
          <p>
            The area is located only a few kilometers from Chania, and here you will also find Aksos Suites, designed with a special focus on guests with disabilities.
          </p>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-bold text-slate-900">⭐ Marathi – Chania</h4>
          <p>
            Marathi is located east of Chania and is another very interesting option.
          </p>
          <p>
            The beach is located in a sheltered bay, and SEATRAC's official information indicates very comprehensive accessibility.
          </p>
          <div>
            <p className="font-semibold text-slate-800 mb-2">Documented facilities:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
              <li>♿ SEATRAC – yes</li>
              <li>♿ Beach track – yes</li>
              <li>♿ Parking – yes</li>
              <li>♿ Toilet – yes</li>
              <li>♿ Changing room – yes</li>
              <li>♿ Shower – yes</li>
              <li>♿ Shade – yes</li>
            </ul>
          </div>
          <p>
            There is also mention of a lifeguard on the beach, which can provide extra peace of mind.
          </p>
          <p>
            According to information from a SEATRAC user, the toilets are located by the restaurants.
          </p>
          <p>
            Marathi can therefore be very interesting as an excursion beach if staying in or around Chania.
          </p>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-bold text-slate-900">⭐ Agios Onoufrios – Chania</h4>
          <p>
            Agios Onoufrios is located northeast of Chania and also has a very complete solution.
          </p>
          <div>
            <p className="font-semibold text-slate-800 mb-2">Documented facilities:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
              <li>♿ SEATRAC – yes</li>
              <li>♿ Beach track – yes</li>
              <li>♿ Parking – yes</li>
              <li>♿ Toilet – yes</li>
              <li>♿ Changing room – yes</li>
              <li>♿ Shower – yes</li>
              <li>♿ Shade – yes</li>
            </ul>
          </div>
          <p>
            This is another great example of how the term "accessible beach" can cover much more than just a wheelchair ramp.
          </p>
          <p>
            Here, there is an opportunity both to cross the beach and continue out into the water.
          </p>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-bold text-slate-900">⭐ Almyrida – Apokoronas</h4>
          <p>
            Almyrida is located east of Chania and is a smaller resort town that can be interesting if you prefer staying somewhere quieter than Chania.
          </p>
          <p>
            Here, too, SEATRAC's official registration shows a complete range of facilities.
          </p>
          <div>
            <p className="font-semibold text-slate-800 mb-2">Documented facilities:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
              <li>♿ SEATRAC – yes</li>
              <li>♿ Beach track – yes</li>
              <li>♿ Parking – yes</li>
              <li>♿ Toilet – yes</li>
              <li>♿ Changing room – yes</li>
              <li>♿ Shower – yes</li>
              <li>♿ Shade – yes</li>
            </ul>
          </div>
          <p>
            Almyrida is therefore one of the areas I would definitely look closer at if seeking a resort town with access to an accessible beach.
          </p>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-bold text-slate-900">⭐ Kalyves – Apokoronas</h4>
          <p>
            Not far from Almyrida lies Kalyves.
          </p>
          <p>
            Kalyves is also registered as a SEATRAC beach and is interesting because you can combine a smaller Cretan holiday village with better beach access.
          </p>
          <p>
            SEATRAC's registry shows beach facilities on site, including access solutions around the facility.
          </p>
          <p>
            As with all beaches, I recommend checking the current status shortly before traveling.
          </p>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-bold text-slate-900">⭐ Rethymnon City Beach</h4>
          <p>
            If you want to combine the beach with a larger town, Rethymnon is particularly interesting.
          </p>
          <p>
            On Rethymnon's long town beach, SEATRAC is available with the following registered:
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
            <li>♿ SEATRAC – yes</li>
            <li>♿ Beach track – yes</li>
            <li>♿ Parking – yes</li>
            <li>♿ Toilet – yes</li>
            <li>♿ Changing room – yes</li>
            <li>♿ Shower – yes</li>
            <li>♿ Shade – yes</li>
          </ul>
          <p>
            It is a really attractive combination, as you can stay in or close to Rethymnon and have restaurants, shops, and town life nearby.
          </p>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-bold text-slate-900">⭐ Gerani – Rethymnon</h4>
          <p>
            At Gerani west of Rethymnon, another very complete solution is found.
          </p>
          <div>
            <p className="font-semibold text-slate-800 mb-2">Documented facilities:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
              <li>♿ SEATRAC – yes</li>
              <li>♿ Beach track – yes</li>
              <li>♿ Parking – yes</li>
              <li>♿ Toilet – yes</li>
              <li>♿ Changing room – yes</li>
              <li>♿ Shower – yes</li>
              <li>♿ Shade – yes</li>
            </ul>
          </div>
          <p>
            The SEATRAC remote control is registered at Fysalida Tavern.
          </p>
          <p>
            It is a small detail, but exactly that kind of information can be very valuable when you are standing on the beach needing to use the system.
          </p>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-bold text-slate-900">⭐ Agia Galini – South Crete</h4>
          <p>
            If you would rather experience Crete's south coast, Agia Galini is well worth a look.
          </p>
          <p>
            Here there is also a very comprehensive SEATRAC solution.
          </p>
          <div>
            <p className="font-semibold text-slate-800 mb-2">Documented facilities:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
              <li>♿ SEATRAC – yes</li>
              <li>♿ Beach track – yes</li>
              <li>♿ Parking – yes</li>
              <li>♿ Toilet – yes</li>
              <li>♿ Changing room – yes</li>
              <li>♿ Shower – yes</li>
              <li>♿ Shade – yes</li>
            </ul>
          </div>
          <p>
            Several local taverns and a café are registered as contact points for SEATRAC.
          </p>
          <p>
            Agia Galini also shows that you don't need to choose the major resort areas on Crete's north coast to find an accessible beach.
          </p>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-bold text-slate-900">⭐ Ligaria – near Heraklion</h4>
          <p>
            Ligaria is situated in a beautiful bay west of Heraklion.
          </p>
          <p>
            Here we once again find virtually the full package.
          </p>
          <div>
            <p className="font-semibold text-slate-800 mb-2">Documented facilities:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
              <li>♿ SEATRAC – yes</li>
              <li>♿ Beach track – yes</li>
              <li>♿ Parking – yes</li>
              <li>♿ Toilet – yes</li>
              <li>♿ Changing room – yes</li>
              <li>♿ Shower – yes</li>
              <li>♿ Shade – yes</li>
              <li className="sm:col-span-2">🛟 Lifeguard – registered as contact point for SEATRAC</li>
            </ul>
          </div>
          <p>
            The latter is interesting because SEATRAC's official site indicates the lifeguard as the point of contact to get the remote control.
          </p>
          <p>
            Ligaria can therefore be an excellent beach to look into if staying near Heraklion or along Crete's central north coast.
          </p>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-bold text-slate-900">⭐ Potamos – Hersonissos area</h4>
          <p>
            There are also options in the Hersonissos area.
          </p>
          <p>
            At Potamos, the following are registered:
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
            <li>♿ SEATRAC – yes</li>
            <li>♿ Beach track – yes</li>
            <li>♿ Parking – yes</li>
            <li>♿ Toilet – yes</li>
            <li>♿ Changing room – yes</li>
            <li>♿ Shower – yes</li>
            <li>♿ Shade – yes</li>
          </ul>
          <p>
            In addition, there are registered SEATRAC beaches at Agkisaras and Arina in the same part of Crete.
          </p>
          <p>
            This makes the Hersonissos area appealing due to multiple options within the same holiday district.
          </p>
        </div>

        <h4 className="text-lg font-bold text-slate-800 mt-6">More SEATRAC beaches in Crete</h4>
        <p>
          SEATRAC's official registry includes even more beaches across Crete.
        </p>
        <p>
          Among those registered are:
        </p>
        <div className="bg-emerald-50/70 border border-emerald-100 p-5 rounded-xl space-y-3 text-sm">
          <div>
            <strong className="text-emerald-950 block mb-1">Chania and Western Crete:</strong>
            <p className="text-emerald-900">Agioi Apostoloi 1 and 2, Marathi, Agios Onoufrios, Almyrida, Kalyves, Nea Chora, and Kissamos.</p>
          </div>
          <div>
            <strong className="text-emerald-950 block mb-1">Rethymnon and Southern Crete:</strong>
            <p className="text-emerald-900">Rethymnon town beach, Gerani, Agia Galini, and Plakias.</p>
          </div>
          <div>
            <strong className="text-emerald-950 block mb-1">Heraklion/Hersonissos:</strong>
            <p className="text-emerald-900">Ligaria, Agkisaras, Arina, and Potamos.</p>
          </div>
          <div>
            <strong className="text-emerald-950 block mb-1">Agios Nikolaos:</strong>
            <p className="text-emerald-900">Kitroplateia.</p>
          </div>
        </div>
        <p>
          However, it is important to be aware that a system may be listed even if it is temporarily uninstalled or out of operation.
        </p>
        <p>
          For example, Kitroplateia is currently listed with parking, beach track, toilet, changing room, shade, and shower, but the equipment itself is marked as uninstalled in SEATRAC's registry.
        </p>
        <p className="font-semibold text-slate-800">
          Therefore, you should never choose a holiday solely based on an old list of SEATRAC beaches.
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">SEATRAC is not the same as beach assistance</h4>
        <p>
          It is also important to distinguish between different types of assistance.
        </p>
        <p>
          On some beaches SEATRAC is present, where the purpose is to provide individuals with reduced mobility more independent access to the sea.
        </p>
        <p>
          Elsewhere, there may be beach wheelchairs and assistance provided by staff or lifeguards.
        </p>
        <p>
          A beach wheelchair typically requires help from another person, whereas SEATRAC was developed with independent sea access in mind.
        </p>
        <p>
          If you need physical help transferring, this should therefore be investigated specifically. You cannot assume that personal assistance is included merely because a beach has SEATRAC.
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">What does "beach track" mean?</h4>
        <p>
          When SEATRAC indicates Beach Track, firm passage across the sand has been established.
        </p>
        <p>
          This is essential because a standard wheelchair often cannot navigate loose sand.
        </p>
        <p>
          Here too, solutions vary. A track may for example lead to SEATRAC and accessible facilities without necessarily meaning you can roll freely along the entire beach.
        </p>
        <p>
          I therefore advise investigating your specific needs beforehand.
        </p>

        <h4 className="text-lg font-bold text-slate-800 mt-6">My top areas of interest</h4>
        <p>
          If I were to start looking for a holiday in Crete where access to the beach and sea is a high priority, I would especially consider:
        </p>
        <div className="space-y-3 bg-stone-50 p-6 rounded-2xl border border-stone-100 text-sm">
          <p><strong className="text-emerald-900">Agioi Apostoloi</strong> – very extensive facilities and two SEATRAC installations.</p>
          <p><strong className="text-emerald-900">Almyrida/Kalyves</strong> – appealing if you prefer a smaller holiday town.</p>
          <p><strong className="text-emerald-900">Rethymnon</strong> – great opportunity to combine town life and the beach.</p>
          <p><strong className="text-emerald-900">Agia Galini</strong> – exciting choice on the south coast.</p>
          <p><strong className="text-emerald-900">Ligaria/Hersonissos area</strong> – multiple accessible beach options along the central north coast.</p>
        </div>
        <p className="italic text-slate-600">
          The best beach always depends on individual preferences and needs.
        </p>

        <div className="bg-amber-50/80 border border-amber-200/70 p-6 rounded-2xl space-y-3">
          <h4 className="text-lg font-bold text-amber-950">Accessible means something different to everyone</h4>
          <p className="text-amber-900 font-medium">
            This is perhaps the single most important takeaway in this entire guide.
          </p>
          <p className="text-amber-900">
            Just because a beach offers SEATRAC, an accessible toilet, and a beach track does not guarantee it fits your exact situation.
          </p>
          <ul className="space-y-1.5 text-sm text-amber-900 pt-2">
            <li>• Can you transfer from your wheelchair independently?</li>
            <li>• Do you need physical assistance?</li>
            <li>• Does your wheelchair need to get right up to the water?</li>
            <li>• Do you require a spacious accessible restroom?</li>
            <li>• Do you need accessible parking very close to the sand?</li>
            <li>• And how will you get from your hotel to the beach?</li>
          </ul>
          <p className="text-amber-900 pt-2 text-sm">
            These questions are just as essential as the SEATRAC equipment itself.
          </p>
        </div>

        <div className="bg-emerald-900 text-white p-6 md:p-8 rounded-2xl space-y-4">
          <h4 className="font-serif font-bold text-xl text-emerald-100">Why I evaluate the entire holiday</h4>
          <p className="text-emerald-50">
            At Alora Rejser, an accessible holiday is not just about finding a hotel with a wheelchair symbol.
          </p>
          <p className="text-emerald-50 font-medium">
            I look at the whole journey.
          </p>
          <p className="text-emerald-100 text-sm">
            The hotel room, the bathroom, bedside clearance, the pool, the hotel surroundings, transfers – and naturally the opportunity to enjoy the beach and the sea.
          </p>
          <p className="text-emerald-100 text-sm">
            Because finding the perfect accessible room is of little value if the beach 300 meters away is unreachable.
          </p>
          <p className="text-emerald-100 text-sm">
            Conversely, a wonderful accessible beach is difficult to enjoy if the route from the hotel is not wheelchair-friendly.
          </p>
          <p className="text-emerald-100 text-sm">
            Therefore, it is so important that you share your needs with me when planning your holiday. Functional levels vary from person to person, and the more I know, the better I can ensure the right fit for you.
          </p>
        </div>

        <div className="bg-stone-50 border border-stone-200/80 p-6 rounded-2xl space-y-3">
          <h4 className="text-base font-bold text-slate-800">Important – always verify status before departure</h4>
          <p className="text-sm text-slate-600">
            SEATRAC is located right by the sea and subject to weather and wave conditions. Equipment may therefore be temporarily taken out of service or uninstalled outside the high season.
          </p>
          <p className="text-sm text-slate-600">
            Details regarding parking, restrooms, boardwalks, etc., indicate registered availability – not necessarily precise measurements, gradients, or suitability for every type of wheelchair.
          </p>
          <p className="text-sm text-slate-600 font-medium">
            At Alora Rejser, I always investigate the concrete conditions tailored to each individual traveler's needs.
          </p>
        </div>

        <p className="pt-4 text-center font-serif italic text-lg text-emerald-800 font-bold">
          Holidays should feel like holidays – for everyone
        </p>
      </div>
    )
  },
  {
    id: 'hvad-skal-du-huske-paa-ferie-som-koerestolsbruger',
    title: 'What to remember on holiday as a wheelchair user?',
    date: 'June 2026',
    category: 'Practical info',
    categories: ['Practical info'],
    intro: 'Planning a holiday as a wheelchair user often requires a bit of extra preparation.',
    image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/rejsetips-hero.jpg',
    imageLarge: 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/rejsetips-hero.jpg',
    routePath: '/en/travel-tips/what-to-remember-on-holiday-as-a-wheelchair-user',
    content: <RejsetipsGuideContent />
  },
  {
    id: 'inspektionsuge-mallorca',
    title: '🌴 My inspection week in Mallorca: 40+ hotels visited personally 🏖️☀️💚',
    date: 'August 2026',
    category: 'Hotel inspections',
    categories: ['Hotel inspections', 'Accessible travel', 'Destinations', 'Practical info'],
    intro: 'Back home after a fantastic and very busy week in Mallorca inspecting more than 40 hotels personally. Read why first-hand verification of accessibility means everything to Alora Rejser.',
    image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/image_2fbecbb8.png',
    imageLarge: 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/image_2fbecbb8.png',
    content: (
      <div className="space-y-6 text-slate-700 leading-relaxed">
        <div className="bg-emerald-50/80 border border-emerald-200/80 p-6 rounded-2xl">
          <p className="text-lg font-serif font-bold text-emerald-950 mb-1">
            🌴 Back home after a fantastic – and very busy – week in Mallorca. 🏖️☀️💚
          </p>
          <p className="text-sm text-emerald-800">
            A personal account of Alora Rejser's first major inspection tour
          </p>
        </div>

        <p>
          The past week has meant long days, many hours on the road, and visits to more than 40 hotels across the island🏨
        </p>

        <p>
          I went all the way around – from Palma, Playa de Palma, Magaluf, Cala Millor to Sa Coma, Alcúdia, Playa de Muro, Can Picafort, and Port de Pollença. Some days I drove from one end of the island to the other, but I loved every single minute😎
        </p>

        <div className="bg-stone-50 border border-stone-200/90 p-6 rounded-2xl space-y-4">
          <h4 className="font-serif font-bold text-slate-900 text-lg">
            Why personal hotel visits?
          </h4>
          <p>
            This was my very first inspection tour, where the goal was to visit hotels to see if they met my high standards and discover new fantastic places🙏🏼
          </p>
          <p>
            Naturally, I was a bit anxious beforehand!
            How would the hotels receive me? Would they have time? Would they understand why I ask so many detailed questions🙈
          </p>
          <p>
            Fortunately, all my worries were put to rest🤗
          </p>
          <p>
            I was met with incredible warmth and helpfulness everywhere🙏🏼
            The hotel staff were fantastic, guided me around, answered all my questions patiently, and did everything they could to give me the best insights.
            I am truly grateful for that❤️
          </p>
        </div>

        <div className="bg-amber-50/70 border border-amber-200/80 p-6 rounded-2xl space-y-3">
          <h4 className="font-serif font-bold text-amber-950 text-lg">
            Less brochure, more reality
          </h4>
          <p>
            Several people asked me why I spend so much time visiting hotels in person when I can read about them online – shouldn't that be enough in the startup phase? And yes, perhaps for some🤔
          </p>
          <p className="font-medium text-slate-900">
            To me, a hotel is much more than pretty photos and a nice description, and I genuinely need to stand 100% behind every single holiday I sell🙏🏼❤️
          </p>
          <p>
            I want to feel the atmosphere. See how staff welcome guests. Walk around the grounds, experience the rooms, and check if accessibility actually works in reality – not just on paper!
          </p>
        </div>

        <p>
          When you live close to a disability in everyday life, you notice completely different things than most people. Those are precisely the details I look for. Is the elevator wide enough? Is the pool easy to enter? How does the bathroom function? Are access routes level? It is the small things that make the biggest difference – and which brochures rarely tell the whole truth about.
        </p>

        <p className="font-medium text-emerald-900 bg-emerald-50/50 p-4 rounded-xl border border-emerald-100">
          Only when I have been there myself can I recommend a hotel with complete peace of mind.
        </p>

        <p>
          And that is also why I actually rejected several hotels on this trip. They looked fine from home, but did not live up to our strict accessibility standards. Conversely, I also found some absolutely magical gems that surprised me positively🤗😎
        </p>

        <p>
          This time I was also searching for something a bit different: Small boutique hotels, charming fincas, and places with authentic soul – places that stand out from conventional mass resorts.
        </p>

        <p>
          That is precisely the type of experiences I have missed finding for my husband and myself, while still offering excellent accessibility🤗
        </p>

        <div className="bg-stone-50 p-6 rounded-2xl border border-stone-200 space-y-3">
          <p>
            Now begins the lovely work of reviewing all photos, videos, and notes. Over the coming days, I will share the hotels visited and provide honest reviews – both places we already partner with and brand new destinations.
          </p>
          <p className="font-semibold text-emerald-950">
            Thank you so much for following along and supporting Alora Rejser. 💚 It means more than you know. I look forward to taking you along on Mallorca – hotel by hotel.
          </p>
        </div>
      </div>
    )
  },
  {
    id: 'midtjyllands-avis-omtale',
    title: 'A major milestone for Alora Rejser! 💚 Featured in Midtjyllands Avis',
    date: 'August 2026',
    category: 'News & press',
    categories: ['News & press', 'Practical info', 'Accessible travel'],
    intro: 'We are incredibly proud that Alora Rejser has been featured in Midtjyllands Avis! Read the story behind Alora Rejser and why we are passionate about making accessible travel easy and worry-free.',
    image: 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/midtjyllands-avis-omtale.jpg',
    imageLarge: 'https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/midtjyllands-avis-omtale.jpg',
    content: (
      <div className="space-y-6 text-slate-700 leading-relaxed">
        <div className="bg-emerald-50/80 border border-emerald-200/80 p-6 rounded-2xl">
          <div className="flex items-center gap-2 text-emerald-800 font-bold text-sm mb-1">
            <Award size={18} />
            <span>Press & feature</span>
          </div>
          <h3 className="text-xl md:text-2xl font-serif font-bold text-emerald-950">
            A major milestone for Alora Rejser! 💚
          </h3>
        </div>

        <p className="text-base sm:text-lg font-medium text-slate-900">
          We are incredibly proud that Alora Rejser has been featured in Midtjyllands Avis🙏🏼
        </p>

        <p>
          The article shares the story behind Alora Rejser – and why we are so dedicated to making it easier for people with disabilities and their families to travel safely and comfortably❤️
        </p>

        <div className="bg-stone-50 border border-stone-200/80 p-6 rounded-2xl space-y-3">
          <p>
            Behind Alora Rejser lies countless hours of hard work, great dedication, and a deep desire to create something that makes a real difference🤗
          </p>
          <p>
            We personally visit hotels and destinations so we can provide honest and thorough guidance on accessibility – because every detail matters🙏🏼❤️🌴
          </p>
        </div>

        <p>
          Thank you to Midtjyllands Avis for telling our story, and thank you to everyone following along and supporting Alora Rejser🥳
          It gives us even more motivation to continue discovering the best accessible travel experiences worldwide🌍✈️
        </p>

        <p className="font-medium text-slate-900">
          We look forward to helping even more people travel – without hassle😍🏖️🌴
        </p>

        <div className="pt-4">
          <a
            href="https://midtjyllandsavis.dk/artikel/76ece63d-ddc3-4233-aa96-d3c3961b716f?fbclid=IwdGRjcATpJL1wZG9mAWZkaWQWUMXXO7NLC20sk4GP_D9caHnKPg4dImV4dG4DYWVtAjExAHNydGMGYXBwX2lkCjY2Mjg1NjgzNzkAAR7iPM2a59UaCyGErV2yX08Mo6W6Fl006jYMj6nCh6US1_DphVK2Azp8yVlVpA_aem_IFNN7Qt_ye-MKDXnGuW8rg"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-emerald-800 text-white font-medium px-6 py-3 rounded-full hover:bg-emerald-900 transition-colors shadow-sm"
          >
            <span>Read the article at Midtjyllands Avis</span>
            <ExternalLink size={16} />
          </a>
        </div>
      </div>
    )
  }
];

// Fallback articles array for backwards compatibility
export const all_articles_da = [...articles_da, ...inspectionArticles_da];
export const all_articles_en = [...articles_en, ...inspectionArticles_en];
export const articles = all_articles_da;

const categories_da = [
  'Alle',
  'Hotelinspektioner',
  'Lufthavn & assistance',
  'Handicaprejser',
  'Destinationer',
  'Nyheder & presse',
  'Praktisk info'
];

const categories_en = [
  'All',
  'Hotel inspections',
  'Airport & assistance',
  'Accessible travel',
  'Destinations',
  'News & press',
  'Practical info'
];

const RejsetipsPage = () => {
  const { language, getLocalizedPath } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(language === 'en' ? 'All' : 'Alle');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const navigate = useNavigate();

  const currentArticles = language === 'en' ? all_articles_en : all_articles_da;
  const currentCategories = language === 'en' ? categories_en : categories_da;

  // Reset category when language changes
  useEffect(() => {
    setActiveCategory(language === 'en' ? 'All' : 'Alle');
  }, [language]);

  // Synkroniser søgning og valgt artikel fra URL
  useEffect(() => {
    const searchFromUrl = searchParams.get('search');
    if (searchFromUrl) {
      setSearchQuery(searchFromUrl);
    }

    const articleId = searchParams.get('article');
    if (articleId) {
      const art = currentArticles.find((a) => a.id === articleId);
      if (art) {
        setSelectedArticle(art);
      }
    } else {
      setSelectedArticle(null);
    }
  }, [searchParams, currentArticles]);

  const handleOpenArticle = (article) => {
    if (article.routePath) {
      navigate(article.routePath);
      return;
    }

    const newParams = new URLSearchParams(searchParams);
    newParams.set('article', article.id);
    setSearchParams(newParams);
  };

  const handleCloseModal = () => {
    setSelectedArticle(null);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('article');
    setSearchParams(newParams);
  };

  // Filtrering af artikler
  const filteredArticles = currentArticles.filter((article) => {
    const isAll = activeCategory === 'Alle' || activeCategory === 'All';
    const matchesCategory =
      isAll ||
      article.category === activeCategory ||
      article.categories?.includes(activeCategory);

    const matchesSearch =
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.intro.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.category.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchParams({ search: searchQuery });
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchParams({});
  };

  const traveltipsPath = getLocalizedPath('traveltips', language);
  const articlePath = selectedArticle
    ? (selectedArticle.routePath || `${traveltipsPath}?article=${selectedArticle.id}`)
    : traveltipsPath;

  const breadcrumbs = [
    {
      name: language === 'en' ? 'Home' : 'Forside',
      url: getLocalizedPath('home', language)
    },
    {
      name: language === 'en' ? 'Travel Tips' : 'Rejsetips',
      url: traveltipsPath
    },
    ...(selectedArticle ? [{
      name: selectedArticle.title,
      url: articlePath
    }] : [])
  ];

  const breadcrumbSchema = buildBreadcrumbSchema(breadcrumbs);
  const organizationSchema = buildOrganizationSchema({ language });

  let pageSchemas = [];
  if (selectedArticle) {
    const articleSchema = buildArticleSchema({
      title: selectedArticle.title,
      description: selectedArticle.intro,
      image: selectedArticle.image,
      imageLarge: selectedArticle.imageLarge,
      datePublished: selectedArticle.date,
      url: articlePath,
      language,
      category: selectedArticle.category,
      categories: selectedArticle.categories
    });
    pageSchemas = [organizationSchema, breadcrumbSchema, articleSchema].filter(Boolean);
  } else {
    const { blog, itemList, blogPostItems } = buildBlogWithArticlesSchema({
      articles: currentArticles,
      language,
      currentUrl: traveltipsPath
    });
    pageSchemas = [organizationSchema, breadcrumbSchema, blog, itemList, ...blogPostItems].filter(Boolean);
  }

  return (
    <div className="pt-24 md:pt-28 relative min-h-screen bg-stone-50/50">
      <SEO 
        title={
          selectedArticle 
            ? selectedArticle.title 
            : (language === 'en' ? "Travel Tips & Guides - Accessible Travel" : "Rejsetips & Guides - Handicapvenlige Rejser")
        } 
        description={
          selectedArticle 
            ? selectedArticle.intro 
            : (language === 'en' 
                ? "Get good advice, guides, and practical travel tips for accessible travel, wheelchair-friendly holidays, and accessible hotels. Read about airport assistance, mobility aids, and much more."
                : "Få gode råd, guides og praktiske rejsetips til handicaprejser, kørestolsvenlige ferier og tilgængelige hoteller. Læs om lufthavnsassistance, hjælpemidler og meget mere.")
        }
        ogImage={selectedArticle ? (selectedArticle.imageLarge || selectedArticle.image) : undefined}
        ogType={selectedArticle ? 'article' : 'website'}
        canonical={articlePath}
        schemaMarkup={pageSchemas}
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-stone-50 pt-4 pb-8 md:py-12 border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center">
            {/* Tekst-indhold */}
            <div className="w-full lg:w-1/2 order-2 lg:order-1">
              <span className="bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full inline-block mb-4">
                {language === 'en' ? 'Inspiration & Knowledge' : 'Inspiration & Viden'}
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-slate-900 mb-3">
                {language === 'en' ? 'Travel Tips' : 'Rejsetips'}
              </h1>
              <p className="text-lg md:text-xl font-serif text-emerald-800 mb-6 italic">
                {language === 'en' 
                  ? 'Practical information and inspiration for accessible travel' 
                  : 'Praktisk information og inspiration til handicapvenlige rejser'}
              </p>
              <div className="text-slate-600 space-y-4 leading-relaxed text-sm md:text-base">
                {language === 'en' ? (
                  <>
                    <p>
                      Here you will find guides, travel tips, and practical information for those seeking accessible travel, wheelchair-friendly holidays, and accessible hotels. We share our experiences here with travel tips for traveling in a wheelchair, airport assistance, transport, mobility aids, accessible beaches, pool lifts, and other factors that can make your holiday easier and safer.
                    </p>
                    <p>
                      Whether you are planning a holiday to Mallorca, Crete, Paris, Austria, or other popular destinations, you will find useful knowledge about accessibility, hotels, sights, and practical matters before departure. Our goal is to make it easier to find accessible holidays and create peace of mind before, during, and after the trip. With the right planning, more people can experience the freedom of travel – without unnecessary worries.
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      Her finder du guides, rejsetips og praktisk information til dig, der søger handicaprejser, kørestolsvenlige ferier og tilgængelige hoteller. Vi deler vores erfaringer her på siden med rejsetips til rejser i kørestol, lufthavnsassistance, transport, hjælpemidler, handicapvenlige strande, poollifte og andre forhold, der kan gøre din ferie lettere og mere tryg.
                    </p>
                    <p>
                      Uanset om du planlægger en ferie til Mallorca, Kreta, Paris, Østrig eller andre populære destinationer, finder du nyttig viden om tilgængelighed, hoteller, seværdigheder og praktiske forhold før afrejse. Vores mål er at gøre det nemmere at finde handicapvenlige rejser og skabe tryghed både før, under og efter rejsen. Med den rette planlægning kan flere opleve friheden ved at rejse – uden unødige bekymringer.
                    </p>
                  </>
                )}
              </div>
            </div>
            
            {/* Billede */}
            <div className="w-full lg:w-1/2 order-1 lg:order-2">
              <div className="relative h-64 sm:h-80 md:h-[400px] rounded-3xl overflow-hidden shadow-md">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/rejsetips-hero.jpg"
                  alt={language === 'en' ? "Quiet beach promenade with accessibility" : "Rolig strandpromenade med tilgængelighed"}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-16 max-w-7xl mx-auto px-6">
        {/* Search and Filters Container */}
        <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-stone-100 mb-12">
          <div className="flex flex-col lg:flex-row gap-6 justify-between items-center">
            {/* Search Field */}
            <form onSubmit={handleSearchSubmit} className="relative w-full lg:w-96 flex items-center">
              <input
                type="text"
                placeholder={language === 'en' ? "Search travel tips..." : "Søg i rejsetips..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-24 py-3.5 bg-stone-50 border border-stone-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#99A18C]/50 focus:border-[#99A18C] text-slate-700 transition-all"
              />
              <Search className="absolute left-4 text-slate-400 w-5 h-5" />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-20 p-1 text-slate-400 hover:text-slate-600"
                >
                  <X size={16} />
                </button>
              )}
              <button
                type="submit"
                className="absolute right-2 px-4 py-2 bg-[#99A18C] hover:bg-[#8a917e] text-white text-xs font-bold rounded-xl transition-colors"
              >
                {language === 'en' ? 'Search' : 'Søg'}
              </button>
            </form>

            {/* Category Filter Buttons (Wrapped, no scroll) */}
            <div className="w-full flex flex-wrap gap-2 justify-start lg:justify-end">
              {currentCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                    activeCategory === cat
                      ? 'bg-[#99A18C] text-white shadow-md shadow-[#99A18C]/15'
                      : 'bg-stone-100 text-slate-600 hover:bg-stone-200/70'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Article Grid */}
        {filteredArticles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article, index) => (
              <motion.article
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onClick={() => handleOpenArticle(article)}
                className="bg-white rounded-3xl overflow-hidden border border-stone-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col cursor-pointer group"
              >
                {/* Image Container */}
                <div className="relative h-56 overflow-hidden bg-stone-100">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                {/* Content Container */}
                <div className="p-6 flex-1 flex flex-col justify-between">
                  <div>
                    <span className="text-xs font-bold text-[#737C66] uppercase tracking-wider block mb-2">
                      {article.category}
                    </span>
                    <h2 className="text-xl font-serif font-bold text-slate-800 mb-3 group-hover:text-[#737C66] transition-colors line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-slate-600 text-sm leading-relaxed mb-6 line-clamp-3">
                      {article.intro}
                    </p>
                  </div>

                  <div className="flex items-center gap-1.5 text-sm font-bold text-[#99A18C] group-hover:text-[#8a917e] transition-colors">
                    {language === 'en' ? 'Read more' : 'Læs mere'}
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-3xl border border-stone-100 p-8">
            <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-700 mb-2">
              {language === 'en' ? 'No articles found' : 'Ingen artikler fundet'}
            </h3>
            <p className="text-slate-500 text-sm max-w-md mx-auto">
              {language === 'en' 
                ? "We couldn't find any articles matching your search or category. Try choosing another category or clearing the search field."
                : "Vi kunne ikke finde nogen artikler, der matcher din søgning eller kategori. Prøv at vælge en anden kategori eller rydde søgefeltet."}
            </p>
            <button
              onClick={() => {
                setActiveCategory(language === 'en' ? 'All' : 'Alle');
                setSearchQuery('');
                setSearchParams({});
              }}
              className="mt-6 px-6 py-2.5 bg-[#99A18C] text-white text-xs font-bold rounded-xl hover:bg-[#8a917e] transition-colors"
            >
              {language === 'en' ? 'Reset filters' : 'Nulstil filtre'}
            </button>
          </div>
        )}

        {/* Bottom CTA Section */}
        <div className="mt-24 bg-[#99A18C]/10 border border-[#99A18C]/20 rounded-[2.5rem] p-8 md:p-16 text-center max-w-4xl mx-auto shadow-sm">
          <div className="w-16 h-16 bg-[#99A18C]/20 rounded-full flex items-center justify-center text-[#737C66] mx-auto mb-6">
            <Heart className="w-8 h-8 fill-current" />
          </div>
          <h3 className="text-2xl md:text-3xl font-serif font-bold text-slate-800 mb-4">
            {language === 'en' ? 'Need help with your trip?' : 'Har du brug for hjælp til din rejse?'}
          </h3>
          <p className="text-slate-600 text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            {language === 'en'
              ? "We are ready to help you find the perfect trip and answer your questions about accessibility and assistance."
              : "Vi sidder klar til at hjælpe dig med at finde den helt rigtige rejse og svare på dine spørgsmål om tilgængelighed og assistance."}
          </p>
          <Link
            to={getLocalizedPath('contact', language)}
            className="inline-flex items-center gap-2 bg-[#99A18C] text-white px-8 py-4 rounded-full font-bold hover:bg-[#8a917e] transition-all transform hover:-translate-y-0.5 shadow-md hover:shadow-lg"
          >
            {language === 'en' ? 'Contact us here →' : 'Kontakt os her →'}
          </Link>
        </div>
      </section>

      {/* Article Detail Modal */}
      <AnimatePresence>
        {selectedArticle && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCloseModal}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            ></motion.div>

            {/* Modal Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 350 }}
              className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-[2rem] shadow-2xl overflow-hidden flex flex-col z-10"
            >
              {/* Close Button */}
              <button
                onClick={handleCloseModal}
                className="absolute top-4 right-4 z-20 p-2.5 bg-white/90 hover:bg-white text-slate-700 hover:text-slate-900 rounded-full shadow-md backdrop-blur-md transition-all cursor-pointer"
                aria-label={language === 'en' ? "Close article" : "Luk artikel"}
              >
                <X size={20} />
              </button>

              {/* Scrollable Container */}
              <div className="overflow-y-auto flex-1 no-scrollbar">
                {/* Hero Header */}
                {selectedArticle.hotelId ? (
                  <div className="bg-gradient-to-br from-stone-900 via-slate-800 to-emerald-950 p-6 md:p-10 text-white relative">
                    <span className="bg-[#99A18C] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full shadow-sm inline-block mb-3">
                      {selectedArticle.category}
                    </span>
                    <h1 className="text-2xl md:text-3xl font-serif font-bold text-white leading-tight pr-10">
                      {selectedArticle.title}
                    </h1>
                  </div>
                ) : (
                  <div className="relative h-64 md:h-96 bg-stone-100">
                    <img
                      src={selectedArticle.imageLarge}
                      alt={selectedArticle.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-slate-950/20 to-transparent"></div>
                    <div className="absolute bottom-6 left-6 right-6 text-white">
                      <span className="bg-[#99A18C] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full shadow-sm inline-block mb-3">
                        {selectedArticle.category}
                      </span>
                      <h1 className="text-2xl md:text-4xl font-serif font-bold text-white leading-tight">
                        {selectedArticle.title}
                      </h1>
                    </div>
                  </div>
                )}

                {/* Article Body */}
                <div className="p-6 md:p-10">
                  {selectedArticle.content}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RejsetipsPage;
